import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import MessageBubble from '../components/chat/MessageBubble';
import { Emo } from '../components/chat/MessageBubble';
import CallModal from '../components/chat/CallModal';
import MeetingModal from '../components/chat/MeetingModal';
import GroupModal from '../components/chat/GroupModal';
import Lightbox from '../components/chat/Lightbox';
import axios from 'axios';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { API_URL, getAuthHeader, formatFileSize, getFileIcon } from '../utils/config';
import useStore from '../store/useStore';
import useSocket from '../hooks/useSocket';
import { encryptMessage, decryptMessage } from '../utils/encryption';
import { getSetting, applySettings } from '../utils/settings';
import { playNotifSound, unlockAudio } from '../utils/notifSound';
import MeetingsCalendar from '../components/chat/MeetingsCalendar';
import ForwardModal from '../components/chat/ForwardModal';
import UserCalendarModal from '../components/chat/UserCalendarModal';
import GroupInfoPanel from '../components/chat/GroupInfoPanel';
import usePushNotifications from '../hooks/usePushNotifications';
import useIsMobile from '../hooks/useIsMobile';
import MobileChatPage from './MobileChatPage';
import SettingsModal from '../components/chat/SettingsModal';
import { getSocket } from '../hooks/useSocket';

function MsgInfoModal({ msgId, conversationId, users, currentUserId, onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    if (!msgId) return;
    axios.get(`${API_URL}/messages/${msgId}/reads`, { headers: getAuthHeader() })
      .then(({ data }) => { setInfo(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [msgId]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 dark:text-white text-sm">ℹ️ Message Info</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <p className="text-center text-gray-400 py-6 text-sm">Loading...</p>
          ) : !info ? (
            <p className="text-center text-gray-400 py-6 text-sm">Could not load info</p>
          ) : (
            <div className="space-y-4">
              {/* Read by */}
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <svg width="14" height="10" viewBox="0 0 16 11" fill="none"><path d="M1 5.5L4.5 9L10 3" stroke="#0d9488" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 5.5L9.5 9L15 3" stroke="#0d9488" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Read by ({(info.read_by || []).length})
                </p>
                {(info.read_by || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No one has read this yet</p>
                ) : (info.read_by || []).map((r, i) => {
                  const u = users.find(x => Number(x.id) === Number(r.user_id));
                  return (
                    <div key={i} className="flex items-center gap-3 py-1.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                        {u?.avatar_url?.startsWith('/uploads/') ? <img src={`https://qtalk.qualtechedge.in${u.avatar_url}`} className="w-full h-full object-cover" alt=""/> : (u?.full_name?.[0] || '?').toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{u?.full_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-400">{r.read_at ? new Date(r.read_at).toLocaleTimeString('en-IN', {hour:'2-digit',minute:'2-digit',timeZone:'Asia/Kolkata'}) : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Delivered to */}
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <svg width="14" height="10" viewBox="0 0 16 11" fill="none"><path d="M1 5.5L4.5 9L10 3" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 5.5L9.5 9L15 3" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Delivered to ({((info.delivered_to || []).filter(d => !new Set((info.read_by || []).map(x => Number(x.user_id))).has(Number(d.user_id || d)))).length})
                </p>
                {(() => {
                  const seenIds = new Set((info.read_by || []).map(x => Number(x.user_id)));
                  const deliveredOnly = (info.delivered_to || []).filter(d => !seenIds.has(Number(d.user_id || d)));
                  if (deliveredOnly.length === 0) {
                    return <p className="text-xs text-gray-400 italic">Everyone has seen this</p>;
                  }
                  return deliveredOnly.map((r, i) => {
                    const u = users.find(x => Number(x.id) === Number(r.user_id || r));
                    return (
                      <div key={i} className="flex items-center gap-3 py-1.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(u?.full_name?.[0] || '?').toUpperCase()}
                        </div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{u?.full_name || 'Unknown'}</p>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const isMobile = useIsMobile();

  const { user, setUser, conversations, setConversations, updateConversation, removeConversation, activeConversation,
          setActiveConversation,
          onlineUsers, setUserOnline, setUserOffline, setOnlineUsersBulk, userStatuses, setUserStatus,
          typingUsers, setTyping, clearTyping, darkMode, toggleDarkMode, logout,
          uploads, addUpload, updateUpload, removeUpload, clearDoneUploads, socketConnected } = useStore();
  // ✅ Local state for messages — NOT in Zustand to avoid cross-render race conditions
  const [messages, setMessages] = useState([]);

  const socket = useSocket();
  // DEBUG: track every setMessages call
  const _setMessages = setMessages;
  const trackedSetMessages = (val) => {
    const arr = typeof val === 'function' ? '(function)' : (Array.isArray(val) ? val.length : typeof val);
    console.trace('🔴 setMessages called with:', arr);
    _setMessages(val);
  };
  const { permission, subscribed, subscribe, unsubscribe } = usePushNotifications();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const draftsRef = useRef({});          // per-conversation unsent drafts
  const prevConvIdRef = useRef(null);    // conversation we're switching away from
  const messageInputRef = useRef('');    // live copy for save-on-switch
  useEffect(() => { messageInputRef.current = messageInput; }, [messageInput]);
  const [showEmoji, setShowEmoji] = useState(false);

  const [activeTab, setActiveTab] = useState('chats');
  const [meetings, setMeetings] = useState([]);
  const [showPanel, setShowPanel] = useState('sidebar');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const generateMeetId = () => user?.meet_link || (Math.random().toString(36).substring(2,8).toUpperCase() + '-' + Math.random().toString(36).substring(2,6).toUpperCase());
  const [viewProfile, setViewProfile] = useState(null);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [profilePanelUser, setProfilePanelUser] = useState(null);
  const [profileTab, setProfileTab] = useState('info');
  const [sharedMedia, setSharedMedia] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: user?.full_name||'', mobile:'', department:'', designation:'', status:'' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const removeAvatar = async () => {
    setShowAvatarMenu(false);
    setAvatarFile(null);
    setAvatarPreview(null);
    if (!user?.avatar_url) return;
    try {
      await axios.delete(`${API_URL}/users/me/avatar`, { headers: getAuthHeader() });
      setUser({ ...user, avatar_url: null });
      toast.success('Photo removed');
    } catch(e) { toast.error('Failed to remove photo'); }
  };
  const [syncStatus, setSyncStatus] = useState({ configured: false, sync_email: null, last_sync: null });
  const [syncForm, setSyncForm] = useState({ email: '', password: '' });
  const [showSyncForm, setShowSyncForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ title:'', description:'', meet_link:'', scheduled_at:'', duration_minutes:30, participant_ids:[] });
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('qtalk_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch(e) { return []; }
  });
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [convMenuOpen, setConvMenuOpen] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  // Unread badge: tab title + favicon dot + PWA app badge (issue #5)
  useEffect(() => {
    const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + (b || 0), 0);
    document.title = totalUnread > 0 ? `(${totalUnread > 99 ? '99+' : totalUnread}) Qtalk` : 'Qtalk';
    try {
      if (navigator.setAppBadge) {
        totalUnread > 0 ? navigator.setAppBadge(totalUnread) : navigator.clearAppBadge && navigator.clearAppBadge();
      }
    } catch(e) {}
    try {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      if (totalUnread > 0) {
        const img = new Image();
        img.onload = () => {
          const cv = document.createElement('canvas'); cv.width = 64; cv.height = 64;
          const ctx = cv.getContext('2d');
          ctx.drawImage(img, 0, 0, 64, 64);
          ctx.beginPath(); ctx.arc(48, 16, 14, 0, 2 * Math.PI);
          ctx.fillStyle = '#ef4444'; ctx.fill();
          ctx.fillStyle = '#fff'; ctx.font = 'bold 18px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(totalUnread > 9 ? '9+' : String(totalUnread), 48, 17);
          link.href = cv.toDataURL('image/png');
        };
        img.src = '/logo.png';
      } else {
        link.href = '/logo.png';
      }
    } catch(e) {}
  }, [unreadCounts]);
  const [notifFilter, setNotifFilter] = useState('all');
  const [callModal, setCallModal] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const prevCallModal = React.useRef(null);
  const [missedCalls, setMissedCalls] = useState(0);
  // eslint-disable-next-line
  // Re-fetch call history whenever a call ends (callModal closes)
  React.useEffect(() => {
    if (prevCallModal.current !== null && callModal === null) {
      setTimeout(() => fetchCallHistory(), 1500); // slight delay to let backend log finish
    }
    prevCallModal.current = callModal;
  // eslint-disable-next-line
  }, [callModal]);

  async function fetchCallHistory(clearBadge = false) {
    try {
      const { data } = await axios.get(`${API_URL}/users/calls/history`, { headers: getAuthHeader() });
      const arr = Array.isArray(data) ? data : [];
      setCallHistory(arr);
      if (!clearBadge) {
        // Only update badge count when NOT clearing (i.e. background fetch)
        const missed = arr.filter(c => c.receiver_id === user?.id && (c.status === 'missed' || c.status === 'no_answer')).length;
        setMissedCalls(missed);
      }
      // If clearBadge, badge is already set to 0 by the tab click handler
    } catch(e) { setCallHistory([]); }
  }
  const activeConvRef = useRef(null);
  const messagesLoadedRef = useRef(false);
  const userIdRef = useRef(user?.id);
  useEffect(() => { userIdRef.current = user?.id; }, [user]);
  // userStatuses comes from Zustand store
  // callModal: { isInitiating, targetUser, callType, call }
  const [isDragging, setIsDragging] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(user?.custom_status || 'online');
  const [searchInChat, setSearchInChat] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [msgActionMenu, setMsgActionMenu] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showMsgInfo, setShowMsgInfo] = useState(null); // message id
  const [pastedImage, setPastedImage] = useState(null); // {file, preview, caption}
  const [showSettings, setShowSettings] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [showUserCalendar, setShowUserCalendar] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState([]);
  const enterSelectMode = (msgId) => { setSelectMode(true); setSelectedMsgIds([msgId]); };
  const toggleMsgSelect = (msgId) => setSelectedMsgIds(prev =>
    prev.includes(msgId) ? prev.filter(x => x !== msgId) : [...prev, msgId]);
  const exitSelectMode = () => { setSelectMode(false); setSelectedMsgIds([]); };

  const messagesEndRef = useRef(null);
  const typingClearTimers = useRef({});
  const firstUnreadRef = useRef(null);
  const openUnreadCountRef = useRef(0);
  const prevMsgCountRef = useRef(0);
  const hasScrolledToUnread = useRef(false);
  const fileInputRef = useRef(null);
  const [showDisconnBanner, setShowDisconnBanner] = useState(false);
  // Reusable in-app confirmation modal (replaces native window.confirm)
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmLabel, danger, onConfirm }
  const askConfirm = (opts) => setConfirmModal(opts);
  useEffect(() => {
    let timer;
    if (!socketConnected) {
      timer = setTimeout(() => setShowDisconnBanner(true), 2500);
    } else {
      setShowDisconnBanner(false);
      const conv = activeConvRef.current;
      if (conv) fetchMessages(conv.id);
    }
    return () => clearTimeout(timer);
  }, [socketConnected]);
  const typingTimeoutRef = useRef(null);
  const chatSearchRef = useRef(null);
  // Plan B: when in-chat search opens, ensure the whole conversation is loaded so the
  // client-side filter can match older (paged-out) messages. One-time full fetch.
  const ensureFullHistoryForSearch = async () => {
    const conv = activeConvRef.current;
    if (!conv || fullHistoryLoadedRef.current) return;
    try {
      const { data } = await axios.get(`${API_URL}/chat/conversations/${conv.id}/messages?limit=10000`, { headers: getAuthHeader() });
      const msgs = Array.isArray(data) ? data : [];
      prependingRef.current = true; // don't yank scroll on this bulk load
      fullHistoryLoadedRef.current = true;
      hasMoreRef.current = false;
      oldestIdRef.current = msgs.length ? msgs[0].id : oldestIdRef.current;
      setMessages(msgs);
      requestAnimationFrame(() => { prependingRef.current = false; });
    } catch(e) {}
  };
  const oldestIdRef = useRef(null);
  const hasMoreRef = useRef(false);
  const pageLoadingRef = useRef(false);
  const fullHistoryLoadedRef = useRef(false);
  const prependingRef = useRef(false);
  const pendingRestoreRef = useRef(null);

  useEffect(() => {
    fetchConversations(); fetchUsers(); fetchMyProfile(); fetchSyncStatus();
    axios.get(`${API_URL}/announcements/unread-count`, { headers: getAuthHeader() })
      .then(r => setUnreadAnnouncements(r.data.unread || 0)).catch(() => {});
    applySettings();
    const handler = () => { applySettings(); setChatBgKey(k => k + 1); };
    window.addEventListener('qtalk-settings-changed', handler);
    return () => window.removeEventListener('qtalk-settings-changed', handler);
  }, []);

  // Sync status from profile
  useEffect(() => {
    if (profileForm.status && profileForm.status !== currentStatus) {
      setCurrentStatus(profileForm.status || 'online');
    }
  }, [profileForm.status]);
  // Anchor scroll after prepending older messages: runs before paint, so the
  // viewport stays locked to the message the user was reading (no jump-to-bottom).
  useLayoutEffect(() => {
    if (pendingRestoreRef.current) {
      const c = document.getElementById('qtalk-chat-messages');
      if (c) {
        const { prevHeight, prevTop } = pendingRestoreRef.current;
        c.scrollTop = prevTop + (c.scrollHeight - prevHeight);
      }
      pendingRestoreRef.current = null;
    }
  }, [messages]);
  useEffect(() => {
    if (messages.length === 0) { prevMsgCountRef.current = 0; return; }
    if (prependingRef.current) { prevMsgCountRef.current = messages.length; prependingRef.current = false; return; }
    if (!hasScrolledToUnread.current) {
      // First load - jump (instant) to first unread or end. No smooth travel.
      hasScrolledToUnread.current = true;
      setTimeout(() => {
        if (firstUnreadRef.current) {
          firstUnreadRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        }
      }, 80);
    } else if (messages.length > prevMsgCountRef.current) {
      // Only scroll to end when a NEW message is appended. In-place updates
      // (reactions, edits, deletes) keep the same length, so they no longer jump.
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMsgCountRef.current = messages.length;
  }, [messages]);
  useEffect(() => {
    if (activeTab === 'meetings') fetchMeetings();
    if (activeTab === 'calls') {
      fetchCallHistory(true);
      setMissedCalls(0);
    }
  }, [activeTab]); // eslint-disable-line

  const fetchSyncStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users/me/sync-status`, { headers: getAuthHeader() });
      setSyncStatus(data);
    } catch(e) {}
  };

  const fetchMyProfile = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users/me`, { headers: getAuthHeader() });
      setProfileForm({
        full_name: data.full_name || '',
        mobile: data.mobile || '',
        department: data.department || '',
        designation: data.designation || '',
        status: data.status || ''
      });
      // Merge with current stored user, always overwrite with fresh server data
      const currentUser = JSON.parse(localStorage.getItem('qtalk_user') || '{}');
      const merged = { ...currentUser, ...data };
      // Explicitly ensure avatar_url is always saved
      if (data.avatar_url) merged.avatar_url = data.avatar_url;
      localStorage.setItem('qtalk_user', JSON.stringify(merged));
      setUser(merged);
    } catch(e) { console.error('Profile fetch failed', e); }
  };
  const setCustomStatus = async (status) => {
    try {
      await axios.patch(`${API_URL}/users/me/status`, { status }, { headers: getAuthHeader() });
      setCurrentStatus(status);
      setShowStatusMenu(false);
      const s = getSocket();
      if (s) s.emit('status_update', { status });
      toast.success('Status updated');
    } catch(e) { toast.error('Failed to update status'); }
  };

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/chat/conversations`, { headers: getAuthHeader() });
      const convs = Array.isArray(data) ? data : [];
      setConversations(convs);
      // Seed unread counts from server
      const counts = {};
      convs.forEach(c => { if (c.unread_count > 0) counts[c.id] = parseInt(c.unread_count); });
      setUnreadCounts(counts);
    } catch(e) {}
  };
  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(data);
      // Initialize status map from fetched users
      const statusMap = {};
      data.forEach(u => { if (u.status) statusMap[u.id] = u.status; });
      setUserStatus(statusMap);
    } catch(e) {}
  };
  const PAGE_SIZE = 50;
  const fetchMessages = async (convId) => {
    messagesLoadedRef.current = false;
    setMessages([]);  // Clear previous conversation's messages immediately on switch
    oldestIdRef.current = null;
    hasMoreRef.current = false;
    fullHistoryLoadedRef.current = false;
    pageLoadingRef.current = false;
    try {
      const { data } = await axios.get(`${API_URL}/chat/conversations/${convId}/messages?limit=${PAGE_SIZE}`, { headers: getAuthHeader() });
      const msgs = Array.isArray(data) ? data : [];
      messagesLoadedRef.current = true;
      // Rows come back oldest->newest (server reverses). Oldest = first row.
      oldestIdRef.current = msgs.length ? msgs[0].id : null;
      hasMoreRef.current = msgs.length === PAGE_SIZE;
      if (msgs.length < PAGE_SIZE) fullHistoryLoadedRef.current = true;
      setMessages(msgs);
    } catch(e) {
      messagesLoadedRef.current = true;
      setMessages([]);
    }
  };

  // Load an older page (scroll-up) using the `before` cursor; prepend, preserve scroll position.
  const loadOlderMessages = async () => {
    const conv = activeConvRef.current;
    if (!conv || pageLoadingRef.current || !hasMoreRef.current || oldestIdRef.current == null) return;
    pageLoadingRef.current = true;
    const container = document.getElementById('qtalk-chat-messages');
    const prevHeight = container ? container.scrollHeight : 0;
    const prevTop = container ? container.scrollTop : 0;
    try {
      const { data } = await axios.get(`${API_URL}/chat/conversations/${conv.id}/messages?limit=${PAGE_SIZE}&before=${oldestIdRef.current}`, { headers: getAuthHeader() });
      const older = Array.isArray(data) ? data : [];
      if (older.length) {
        prependingRef.current = true;
        oldestIdRef.current = older[0].id;
        hasMoreRef.current = older.length === PAGE_SIZE;
        setMessages(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          const existing = new Set(safePrev.map(m => m.id));
          const dedup = older.filter(m => !existing.has(m.id));
          return [...dedup, ...safePrev];
        });
        // Restore scroll synchronously before paint via useLayoutEffect (no flicker).
        pendingRestoreRef.current = { prevHeight, prevTop };
      } else {
        hasMoreRef.current = false;
      }
    } catch(e) {
      // leave hasMore as-is; user can retry by scrolling
    } finally {
      pageLoadingRef.current = false;
    }
  };

  const onMessagesScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop < 80 && hasMoreRef.current && !pageLoadingRef.current) {
      loadOlderMessages();
    }
  };

  // ✅ Placed AFTER fetchMessages so it's not undefined when effect runs
  useEffect(() => {
    // Per-chat drafts: stash what was typed in the previous chat, restore this chat's draft
    if (prevConvIdRef.current != null) {
      draftsRef.current[prevConvIdRef.current] = messageInputRef.current || '';
    }
    activeConvRef.current = activeConversation;
    hasScrolledToUnread.current = false;
    firstUnreadRef.current = null;
    const convId = activeConversation ? activeConversation.id : null;
    prevConvIdRef.current = convId;
    setMessageInput(convId != null ? (draftsRef.current[convId] || '') : '');
    if (convId) {
      fetchMessages(convId);
      setUnreadCounts(prev => { openUnreadCountRef.current = prev[convId] || 0; return { ...prev, [convId]: 0 }; });
      // Clear bell notifications for this conversation
      setNotifications(prev => {
        const updated = prev.map(n =>
          n.conversation_id === convId ? { ...n, read: true } : n
        );
        // Recount unread from updated list
        const stillUnread = updated.filter(n => !n.read).length;
        setUnreadNotifs(stillUnread);
        return updated;
      });
      const s = getSocket();
      if (s) {
        s.emit('join_conversation', convId);
        s.emit('mark_read', { conversation_id: convId });
      }
    }
  // eslint-disable-next-line
  }, [activeConversation]);
  const [mentionMembers, setMentionMembers] = useState([]);
  const [chatBgKey, setChatBgKey] = useState(0);
  const getChatBg = () => {
    const WALLPAPER_MAP = {
      wp1: '#f0fdf4',
      wp2: 'linear-gradient(135deg,#dbeafe,#ede9fe)',
      wp3: 'linear-gradient(135deg,#fef3c7,#fde68a)',
      wp4: 'linear-gradient(135deg,#fce7f3,#fbcfe8)',
      wp5: 'linear-gradient(135deg,#ecfdf5,#d1fae5)',
      wp6: 'linear-gradient(135deg,#1e293b,#0f172a)',
      none: '',
    };
    const customWp = getSetting('wallpaperCustom', '');
    const wp = getSetting('wallpaper', 'none');
    if (customWp) return { background: `url(${customWp}) center/cover no-repeat` };
    const bg = WALLPAPER_MAP[wp] || (darkMode ? '#030712' : 'radial-gradient(ellipse at top, #f0fdf8 0%, #f8fafc 100%)');
    return { background: bg };
  };
  // Fetch group members for mention
  useEffect(() => {
    if (activeConversation?.type === 'group') {
      axios.get(`${API_URL}/chat/conversations/${activeConversation.id}/members`, { headers: getAuthHeader() })
        .then(({ data }) => setMentionMembers(data))
        .catch(() => {});
    } else {
      setMentionMembers([]);
    }
  }, [activeConversation]);

  const fetchMeetings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/meetings`, { headers: getAuthHeader() });
      setMeetings(data);
      // Seed bell notifications for pending invites not yet actioned
      const pending = data.filter(m => m.my_status === 'pending' && !m.is_organizer);
      if (pending.length > 0) {
        setNotifications(prev => {
          // Don't duplicate — only add ones not already in the list
          const existingIds = new Set(prev.filter(n => n.type === 'meeting').map(n => n.meeting_id));
          const newNotifs = pending
            .filter(m => !existingIds.has(m.id))
            .map(m => ({
              id: Date.now() + m.id,
              type: 'meeting',
              title: `Meeting Invite: ${m.title}`,
              body: `${m.organizer_name} invited you · ${new Date(m.scheduled_at).toLocaleString('en-IN',{timeZone:'Asia/Kolkata',month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}`,
              time: new Date(m.scheduled_at),
              read: false,
              meeting_id: m.id,
              avatar: '📅'
            }));
          // Seeded (fetch-time) notifications appear in the panel but don't ring the badge —
          // only live events increment it. Prevents stale counts resurrecting on every load.
          return [...newNotifs.map(n => ({ ...n, read: true })), ...prev];
        });
      }
    } catch(e) {}
  };
  const fetchSharedMedia = async (convId) => {
    try {
      const { data } = await axios.get(`${API_URL}/chat/conversations/${convId}/messages?limit=200`, { headers: getAuthHeader() });
      setSharedMedia(data.filter(m => m.message_type === 'image' || m.message_type === 'file'));
    } catch(e) {}
  };

  // Real-time notifications via socket
  useEffect(() => {
    // Socket may not be ready on first render — wait for it
    let s = getSocket();
    let attempts = 0;
    const register = () => {
      s = getSocket();
      if (!s || !userIdRef.current) {
        if (attempts++ < 30) setTimeout(register, 300);
        return;
      }
      attachListeners(s);
    };

    const handleNewMessage = (msg) => {
      const currentConv = activeConvRef.current;
      const isCurrentConv = currentConv && msg.conversation_id === currentConv.id;
      const isOwnMessage = String(msg.sender_id) === String(user?.id);

      // First message from a brand-new conversation: it's not in the list yet,
      // so updateConversation below would do nothing — pull the list to surface it.
      const convsNow = useStore.getState().conversations || [];
      const known = convsNow.some(cv => Number(cv.id) === Number(msg.conversation_id));
      if (!known) fetchConversations();

      if (isCurrentConv) {
        // Always append — dedup by id prevents duplicates
        setMessages(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          if (safePrev.find(m => m.id === msg.id)) return safePrev;
          return [...safePrev, msg];
        });
        setTimeout(() => {
          const el = document.getElementById('messages-end');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 50);
        // Mark as read immediately if it's not our own message
        if (!isOwnMessage) {
          const s = getSocket();
          if (s) s.emit('mark_read', { conversation_id: msg.conversation_id });
          // Play sound for incoming message in active chat
          const dndActive = getSetting('dndEnabled', false);
          const activeSoundName = getSetting('msgSound', 'Default');
          if (!dndActive && activeSoundName !== 'None') try { playNotifSound(activeSoundName); } catch(e) {}
        }
      } else if (!isOwnMessage) {
        // Only notify if it's NOT our own message (e.g. forwarded by us)
        setNotifications(prev => [{
          id: Date.now(),
          type: 'message',
          title: msg.sender_name,
          body: msg.message_type === 'text' ? decryptMessage(msg.content)?.substring(0,60) : '📎 File',
          time: new Date(),
          read: false,
          conversation_id: msg.conversation_id,
          avatar: msg.sender_name?.[0]?.toUpperCase()
        }, ...prev.slice(0,19)]);
        setUnreadNotifs(n => n+1);
        const dnd = getSetting('dndEnabled', false);
        const soundName = getSetting('notifSound', 'Default');
        if (!dnd && soundName !== 'None') try { playNotifSound(soundName); } catch(e) {}
        setUnreadCounts(prev => ({
          ...prev,
          [msg.conversation_id]: (prev[msg.conversation_id] || 0) + 1
        }));

      }
      // ✅ Update conversation preview using store's updateConversation (safe, no wipe)
      updateConversation({
        id: msg.conversation_id,
        last_message: msg.content || msg.file_name,
        last_message_time: msg.created_at,
        last_message_type: msg.message_type,
        last_message_is_mine: Number(msg.sender_id) === Number(user?.id),
        last_sender_id: msg.sender_id,
        last_message_read_by_others: false,
        last_sender_name: msg.sender_name,
        last_message_deleted: false,
      });
    };

    const handleStatusUpdate = (data) => {};

    const handleUserStatusChanged = (data) => {
      // Update real-time status map
      setUserStatus(prev => ({ ...prev, [data.user_id]: data.status }));
      // Update profilePanelUser if currently viewing that person
      setProfilePanelUser(prev => {
        if (prev && prev.id === data.user_id) {
          return { ...prev, status: data.status };
        }
        return prev;
      });
      // Update users list
      setUsers(prev => prev.map(u =>
        u.id === data.user_id ? { ...u, status: data.status } : u
      ));
    };

    const handleTaskReminder = (data) => {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'task',
        title: '⏰ Task Reminder',
        body: data.message,
        time: new Date(),
        read: false
      }, ...prev.slice(0,19)]);
      setUnreadNotifs(n => n+1);
      toast(data.message, { duration:8000, icon:'⏰' });
    };

    const handleTaskAssigned = (data) => {
      const priorityIcon = data.priority==='high' ? '🔴' : data.priority==='medium' ? '🟡' : '🟢';
      setNotifications(prev => [{
        id: Date.now(),
        type: 'task_assigned',
        title: `${priorityIcon} New Task Assigned`,
        body: data.message,
        time: new Date(),
        read: false,
        link: '/todos'
      }, ...prev.slice(0,19)]);
      setUnreadNotifs(n => n+1);
      toast(`${priorityIcon} ${data.message}`, { duration:8000, icon:'✅' });
    };

    const handleMemberLeft = ({ conversation_id, user_id: leftUserId }) => {
      const storedUser = JSON.parse(localStorage.getItem('qtalk_user') || '{}');
      if (leftUserId === storedUser?.id) {
        // I left — remove from my conversations list
        setConversations(prev => prev.filter(c => c.id !== conversation_id));
        const currentConv = activeConvRef.current;
        if (currentConv?.id === conversation_id) setActiveConversation(null);
      }
    };

    const handleUserOnline = ({ user_id }) => {
      setUserOnline(user_id);
    };
    const handleUserOffline = ({ user_id }) => {
      setUserOffline(user_id);
    };
    const handleOnlineUsersList = ({ user_ids }) => {
      setOnlineUsersBulk(user_ids);
    };
    const handleMessageEdited = ({ id, conversation_id, content, edited }) => {
      const currentConv = activeConvRef.current;
      if (!currentConv || Number(currentConv.id) !== Number(conversation_id)) return;
      setMessages(prev => (Array.isArray(prev) ? prev : []).map(m =>
        Number(m.id) === Number(id) ? { ...m, content, edited: true } : m
      ));
    };
    const handleMessageReaction = ({ message_id, conversation_id, reactions, by_user_id }) => {
      const myId = user?.id || JSON.parse(localStorage.getItem('qtalk_user') || '{}')?.id;
      const currentConv = activeConvRef.current;
      if (!currentConv || Number(currentConv.id) !== Number(conversation_id)) return;
      setMessages(prev => (Array.isArray(prev) ? prev : []).map(m => {
        if (Number(m.id) !== Number(message_id)) return m;
        // Merge server counts with our own reacted_by_me state
        const mine = new Set((m.reactions || []).filter(r => r.reacted_by_me).map(r => r.emoji));
        if (Number(by_user_id) === Number(myId)) {
          // our own action already reflected via the local toggle+refetch path; still sync counts
        }
        const merged = (reactions || []).map(r => ({
          emoji: r.emoji, count: Number(r.count),
          reacted_by_me: mine.has(r.emoji)
        }));
        return { ...m, reactions: merged.length ? merged : null };
      }));
    };
    const handleReactionNotify = ({ conversation_id, emoji, by_name }) => {
      setNotifications(prev => [{
        id: Date.now(), type: 'message', title: `${by_name} reacted ${emoji}`,
        body: 'to your message', time: new Date(), read: false,
        conversation_id, avatar: emoji
      }, ...prev]);
      setUnreadNotifs(n => n + 1);
      try { playNotifSound(); } catch(e) {}
    };
    const handleMessagesRead = ({ conversation_id, read_by, all_read }) => {
      // Update messages in current conv to show blue ticks
      // Read id from localStorage as fallback — closure `user` may be null before the store hydrates
      const myId = user?.id || JSON.parse(localStorage.getItem('qtalk_user') || '{}')?.id;
      const currentConv = activeConvRef.current;
      // Blue ticks only when ALL recipients have read (all_read from server).
      // Fallback: if all_read is undefined (older server), keep previous behavior.
      const flipBlue = (typeof all_read === 'undefined') ? true : !!all_read;
      if (flipBlue && currentConv && Number(currentConv.id) === Number(conversation_id) && String(read_by) !== String(myId)) {
        setMessages(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          return safePrev.map(m =>
            String(m.sender_id) === String(myId)
              ? { ...m, read_by_others: true }
              : m
          );
        });
      }
      // Sidebar tick: flip only when all recipients have read
      if (flipBlue && String(read_by) !== String(myId)) {
        // Only flip the sidebar tick if the LAST message is ours
        const convRow = (useStore.getState().conversations || []).find(cv => Number(cv.id) === Number(conversation_id));
        const lastIsMine = convRow && (convRow.last_message_is_mine || Number(convRow.last_sender_id) === Number(myId));
        if (lastIsMine) updateConversation({ id: conversation_id, last_message_read_by_others: true });
      }
      // Only reset unread count if WE are the one who read (not when others read our messages)
      if (String(read_by) === String(myId)) {
        setUnreadCounts(prev => ({ ...prev, [conversation_id]: 0 }));
      }
    };
    const handleUserTyping = ({ user_id, full_name, conversation_id }) => {
      setTyping(conversation_id, user_id, full_name);
      // Auto-clear after 4s in case stop event is missed.
      // Reset any existing timer so rapid typing events don't stack timeouts (which caused flicker).
      if (typingClearTimers.current[conversation_id]) {
        clearTimeout(typingClearTimers.current[conversation_id]);
      }
      typingClearTimers.current[conversation_id] = setTimeout(() => {
        clearTyping(conversation_id);
        delete typingClearTimers.current[conversation_id];
      }, 4000);
    };
    const handleUserStopTyping = ({ conversation_id }) => {
      clearTyping(conversation_id);
    };
    const handleAnnouncementComment = (data) => {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'announcement_comment',
        title: '💬 New Comment',
        body: data.body,
        time: new Date(),
        read: false,
        link: `/announcements#ann-${data.announcement_id}`
      }, ...prev.slice(0,19)]);
      setUnreadNotifs(n => n+1);
      toast(data.body, { duration: 5000, icon: '💬' });
    };

    const handleMentionedInAnnouncement = (data) => {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'mention',
        title: '🔔 You were mentioned',
        body: data.body,
        time: new Date(),
        read: false,
        link: '/announcements'
      }, ...prev.slice(0,19)]);
      setUnreadNotifs(n => n+1);
      setUnreadAnnouncements(n => n+1);
      toast(data.body, { duration:6000, icon:'🔔' });
    };

    const handleNewAnnouncement = (data) => {
      // Don't notify the poster
      if (String(data.poster_id) === String(userIdRef.current)) return;
      setNotifications(prev => [{
        id: Date.now(),
        type: 'announcement',
        title: data.title,
        body: data.body,
        time: new Date(),
        read: false,
        avatar: '📢'
      }, ...prev.slice(0,19)]);
      setUnreadNotifs(n => n + 1);
      setUnreadAnnouncements(n => n + 1);
      toast(data.body, { duration: 5000, icon: '📢' });
    };

    const handleGroupMessageNotify = (data) => {
      // Unread count is already handled by new_message handler — skip to avoid double count
      return;
    };

    const handleMeetingInvite = (data) => {
      // Add to bell notifications
      setNotifications(prev => [{
        id: Date.now(),
        type: 'meeting',
        title: `Meeting Invite: ${data.title}`,
        body: `${data.organizer_name} invited you · ${new Date(data.scheduled_at).toLocaleString('en-IN',{timeZone:'Asia/Kolkata',month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}`,
        time: new Date(),
        read: false,
        meeting_id: data.meeting_id,
        avatar: '📅'
      }, ...prev.slice(0,19)]);
      setUnreadNotifs(n => n + 1);
      // Refresh meetings list so it shows in sidebar
      fetchMeetings();
      // Toast
      toast(`Meeting invite: ${data.title}`, { duration: 6000, icon: '📅' });
    };

    const handleReconnectSync = () => {
      // Socket reconnected after tab sleep/network loss — catch up on anything missed
      fetchConversations();
      const cc = activeConvRef.current;
      if (cc) fetchMessages(cc.id);
    };
    const handleMessageDeleted = ({ message_id, conversation_id }) => {
      const currentConv = activeConvRef.current;
      if (currentConv && Number(conversation_id) === Number(currentConv.id)) {
        // Mark deleted in-place in the open chat, and update sidebar if it was the latest message
        setMessages(prev => {
          const arr = Array.isArray(prev) ? prev : [];
          const updated = arr.map(m => Number(m.id) === Number(message_id)
            ? { ...m, is_deleted: true, content: 'This message was deleted', reactions: null }
            : m);
          const last = updated[updated.length - 1];
          if (last && Number(last.id) === Number(message_id)) {
            updateConversation({ id: conversation_id, last_message: 'This message was deleted', last_message_deleted: true });
          }
          return updated;
        });
      } else {
        // Conversation not open — refetch list so the sidebar preview is accurate
        fetchConversations();
      }
    };
    const attachListeners = (s) => {
      s.off('new_message', handleNewMessage);
      s.off('task_reminder', handleTaskReminder);
      s.off('member_left', handleMemberLeft);
      s.on('member_left', handleMemberLeft);
      s.off('user_online', handleUserOnline);
      s.off('user_offline', handleUserOffline);
      s.off('online_users_list', handleOnlineUsersList);
      s.off('messages_read', handleMessagesRead);
      s.off('message_reaction', handleMessageReaction);
      s.off('message_edited', handleMessageEdited);
      s.off('reaction_notify', handleReactionNotify);
      s.off('user_typing', handleUserTyping);
      s.off('user_stop_typing', handleUserStopTyping);
      s.off('meeting_invite', handleMeetingInvite);

      s.on('new_message', handleNewMessage);
      s.on('task_reminder', handleTaskReminder);
      s.off('message_deleted', handleMessageDeleted);
      s.on('message_deleted', handleMessageDeleted);
      s.off('connect', handleReconnectSync);
      s.on('connect', handleReconnectSync);

      s.off('incoming_call');
      s.off('call_busy');
      s.on('call_busy', () => {
        // User is busy - callModal will handle the busy tone
      });
      s.on('incoming_call', ({ from, fromName, offer, callType, fromAvatar }) => {
        setCallModal(prev => {
          if (prev) return prev;
          const callerUser = users.find(u => Number(u.id) === Number(from)) || { id: from, full_name: fromName, avatar_url: fromAvatar };
          return { isInitiating: false, targetUser: callerUser, callType, call: { offer } };
        });
      });
      s.on('user_online', handleUserOnline);
      s.on('user_offline', handleUserOffline);
      s.on('online_users_list', handleOnlineUsersList);
      s.on('messages_read', handleMessagesRead);
      s.on('message_reaction', handleMessageReaction);
      s.on('message_edited', handleMessageEdited);
      s.on('reaction_notify', handleReactionNotify);
      s.on('user_typing', handleUserTyping);
      s.on('user_stop_typing', handleUserStopTyping);
      s.off('meeting_invite', handleMeetingInvite);
      s.on('meeting_invite', handleMeetingInvite);
      s.off('new_announcement', handleNewAnnouncement);
      s.on('new_announcement', handleNewAnnouncement);
      s.off('mentioned_in_announcement', handleMentionedInAnnouncement);
      s.on('mentioned_in_announcement', handleMentionedInAnnouncement);
      s.off('announcement_comment', handleAnnouncementComment);
      s.on('announcement_comment', handleAnnouncementComment);
      s.off('announcement_reaction');
      s.on('announcement_reaction', (data) => {
        setNotifications(prev => [{
          id: Date.now(), type: 'announcement', read: false, time: new Date(),
          title: '❤️ Someone liked your post',
          body: data.body || 'Someone reacted to your announcement',
          link: '/announcements'
        }, ...prev.slice(0,19)]);
        setUnreadNotifs(n => n + 1);
      });
      s.off('group_message_notify', handleGroupMessageNotify);
      s.on('group_message_notify', handleGroupMessageNotify);

      // Request current online users immediately
      s.emit('get_online_users');
    };

    register(); // start trying to attach listeners

    return () => {
      const sock = getSocket();
      if (sock) {
        sock.off('new_message', handleNewMessage);
        sock.off('task_reminder', handleTaskReminder);
        sock.off('task_assigned', handleTaskAssigned);
        sock.off('member_left', handleMemberLeft);
        sock.off('user_online', handleUserOnline);
        sock.off('user_offline', handleUserOffline);
        sock.off('online_users_list', handleOnlineUsersList);
        sock.off('messages_read', handleMessagesRead);
        sock.off('user_typing', handleUserTyping);
        sock.off('user_stop_typing', handleUserStopTyping);
        sock.off('meeting_invite', handleMeetingInvite);
        sock.off('new_announcement', handleNewAnnouncement);
        sock.off('mentioned_in_announcement', handleMentionedInAnnouncement);
        sock.off('announcement_comment', handleAnnouncementComment);
        sock.off('user_status_changed', handleUserStatusChanged);
        sock.off('group_message_notify', handleGroupMessageNotify);
      }
    };
  }, []); // empty deps — uses refs inside handlers, safe from stale closure

  // Persist notifications to localStorage
  useEffect(() => {
    try { localStorage.setItem('qtalk_notifications', JSON.stringify(notifications.slice(0,50))); } catch(e) {}
  }, [notifications]);

  const markAllNotifsRead = () => { setNotifications(prev => prev.map(n => ({...n, read:true}))); setUnreadNotifs(0); };

  const startGroupCall = () => {
    if (!activeConversation || activeConversation.type !== 'group') return;
    const storedUser = JSON.parse(localStorage.getItem('qtalk_user') || '{}');
    const meetLink = storedUser?.meet_link || user?.meet_link;
    if (!meetLink) { toast.error('Meeting room not set up. Please update your profile.'); return; }
    const meetUrl = `https://qtalk.qualtechedge.in/meet/${meetLink}`;
    const msgText = `📹 ${user?.full_name || storedUser.full_name} has started a group call. Join here: ${meetUrl}`;
    const s = getSocket();
    if (s) {
      s.emit('send_message', {
        conversation_id: activeConversation.id,
        content: encryptMessage(msgText),
        message_type: 'text'
      });
    }
    // Open meet room for the caller
    window.open(meetUrl, '_blank');
    toast.success('Group call started! Meeting link sent to the group.');
  };

  const startCall = (type, targetUserOverride = null) => {
    if (callModal) return; // already in a call
    // Expose active conv id so CallModal can send group invite messages
    window.__qtalk_active_conv_id__ = activeConversation?.id || null;
    const target = targetUserOverride || activeConversation?.other_user;
    if (!target) return;
    const enrichedTarget = users.find(u => Number(u.id) === Number(target.id)) || target;
    setCallModal({ isInitiating: true, targetUser: enrichedTarget, callType: type });
  };

  const [profileShowMessage, setProfileShowMessage] = useState(false);

  const openProfilePanel = async (u, showMsgBtn=false) => {
    setProfilePanelUser(u); setProfileTab('info');
    setShowProfilePanel(true); setProfileShowMessage(showMsgBtn);
    setSharedMedia([]);
    if (activeConversation && (activeConversation.other_user?.id === u.id || activeConversation.type==='group')) {
      fetchSharedMedia(activeConversation.id);
    }
    try {
      const { data } = await axios.get(`${API_URL}/users/${u.id}`, { headers: getAuthHeader() });
      setProfilePanelUser(data);
    } catch(e) {}
  };

  const startChat = async (userId) => {
    // Open a DRAFT chat — no DB conversation is created until the first message/file is sent.
    setMessages([]);
    setShowProfilePanel(false);
    setProfilePanelUser(null);
    const me = user || JSON.parse(localStorage.getItem('qtalk_user') || '{}');
    const isSelf = Number(userId) === Number(me?.id);
    const targetUser = isSelf
      ? { id: me.id, full_name: me.full_name, avatar_url: me.avatar_url }
      : users.find(u => u.id === userId);
    setActiveConversation({ id: null, isDraft: true, type: isSelf ? 'self' : 'direct',
      name: isSelf ? 'You (Saved messages)' : targetUser?.full_name,
      avatar_url: targetUser?.avatar_url, other_user: targetUser });
    setActiveTab('chats'); setShowPanel(null);
  };
  // Materialize a draft conversation on first send. Returns the real conversation_id, or null on failure.
  const ensureConversation = async () => {
    if (!activeConversation) return null;
    if (!activeConversation.isDraft && activeConversation.id) return activeConversation.id;
    if (!activeConversation.other_user?.id) return activeConversation.id || null;
    try {
      const { data } = await axios.post(`${API_URL}/chat/conversations/direct`,
        { user_id: activeConversation.other_user.id }, { headers: getAuthHeader() });
      const realConv = { ...activeConversation, id: data.conversation_id, isDraft: false };
      setActiveConversation(realConv);
      activeConvRef.current = realConv;
      const s = getSocket();
      if (s) s.emit('join_conversation', data.conversation_id);
      // Await so the conversation is in the list BEFORE the message is sent —
      // otherwise the new_message preview update has nothing to update (race → "No messages yet")
      await fetchConversations();
      return data.conversation_id;
    } catch(e) {
      toast.error('Failed to start chat');
      return null;
    }
  };

  const createGroup = async (groupName, description, memberIds, avatarFile) => {
    try {
      const { data } = await axios.post(`${API_URL}/chat/conversations/group`,
        { name: groupName, description, member_ids: memberIds },
        { headers: getAuthHeader() }
      );
      // Upload avatar if provided
      if (avatarFile) {
        const fd = new FormData();
        fd.append('avatar', avatarFile);
        await axios.post(`${API_URL}/chat/conversations/${data.conversation_id}/avatar`, fd,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        ).catch(() => {});
      }
      await fetchConversations();
      // Get full conversation data with avatar_url from refreshed list
      setShowGroupModal(false);
      toast.success('Group created!');
      // Small delay to let fetchConversations update store, then set active
      setTimeout(() => {
        const { conversations: convs } = require('../store/useStore').default.getState();
        const newConv = convs.find(c => c.id === data.conversation_id);
        if (newConv) setActiveConversation(newConv);
        else setActiveConversation({ id: data.conversation_id, type: 'group', name: groupName });
      }, 500);
    } catch(e) { toast.error('Failed to create group'); throw e; }
  };

  const sendMessage = useCallback(async () => {
    if (!messageInput.trim() || !activeConversation) return;
    if (editingMsg) { saveEdit(); return; }
    const s = getSocket();
    if (!s) return toast.error('Not connected');
    const convId = await ensureConversation();
    if (!convId) return;
    const plainText = messageInput.trim();
    const msgContent = encryptMessage(plainText);

    s.emit('send_message', {
      conversation_id: convId,
      content: msgContent,
      message_type: 'text',
      reply_to_id: replyTo?.id || null
    });
    setMessageInput(''); setShowEmoji(false); setReplyTo(null);
    if (activeConversation) delete draftsRef.current[activeConversation.id];
  }, [messageInput, activeConversation, editingMsg, replyTo, user]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false); };
  const handleDrop = async (e) => {
    e.preventDefault(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length || !activeConversation) return;
    // All dropped files → preview/confirm flow (per-file captions)
    setPendingFiles(files);
    setFileCaptions({});
    setPendingIndex(0);
    setFileMentionQuery(null);
    return;
    // Multiple dropped files → upload all sequentially (staggered), matching handleFileUpload
    const dropConvId = await ensureConversation();
    if (!dropConvId) return;
    files.forEach((file, idx) => {
      setTimeout(async () => {
        const uploadId = Date.now() + idx;
        addUpload({ id: uploadId, fileName: file.name, progress: 0, conversationId: dropConvId, convName: activeConversation.name, status: 'uploading' });
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('conversation_id', dropConvId);
          const { data } = await axios.post(`${API_URL}/files/upload`, formData, {
            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (p) => updateUpload(uploadId, { progress: Math.round((p.loaded * 100) / p.total) })
          });
          const s = getSocket();
          if (s) s.emit('send_message', {
            conversation_id: dropConvId,
            message_type: file.type.startsWith('image/') ? 'image' : 'file',
            content: null,
            file_url: data.file_url, file_name: data.file_name,
            file_size: data.file_size, file_mime_type: data.file_mime_type
          });
          updateUpload(uploadId, { status: 'done', progress: 100 });
          setTimeout(() => removeUpload(uploadId), 3000);
        } catch(err) {
          updateUpload(uploadId, { status: 'error' });
          toast.error(err.response?.data?.message || `Failed to send ${file.name}`);
          setTimeout(() => removeUpload(uploadId), 4000);
        }
      }, idx * 500);
    });
    toast.success(`Sending ${files.length} files...`);
  };

  const handleKeyDown = (e) => {
    const enterSends = getSetting('enterSends', true);
    if (e.key === 'Enter' && !e.shiftKey && enterSends) { e.preventDefault(); sendMessage(); }
    if (e.key === 'Enter' && e.shiftKey) { /* new line */ }
    if (e.key === 'Escape') {
      if (showMentions) { setShowMentions(false); return; }
      setReplyTo(null); setEditingMsg(null); setMessageInput('');
    }
    if (showMentions) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i+1, filteredMentions.length-1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => Math.max(i-1, 0)); }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (filteredMentions[mentionIndex]) { e.preventDefault(); insertMention(filteredMentions[mentionIndex]); return; }
      }
    }
  };

  const handlePaste = async (e) => {
    if (!activeConversation) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        // Mac screenshots can be image/tiff — convert to PNG blob for compatibility
        if (item.type === 'image/tiff') {
          try {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width; canvas.height = img.height;
              canvas.getContext('2d').drawImage(img, 0, 0);
              canvas.toBlob(blob => {
                const pngFile = new File([blob], `screenshot_${Date.now()}.png`, { type: 'image/png' });
                const preview = URL.createObjectURL(pngFile);
                setPastedImage({ file: pngFile, preview, caption: '' });
                URL.revokeObjectURL(url);
              }, 'image/png');
            };
            img.src = url;
          } catch(e) { toast.error('Could not process screenshot'); }
          return;
        }
        const ext = item.type === 'image/png' ? '.png' : item.type === 'image/jpeg' ? '.jpg' : item.type === 'image/gif' ? '.gif' : '.png';
        const namedFile = new File([file], `screenshot_${Date.now()}${ext}`, { type: item.type });
        const preview = URL.createObjectURL(namedFile);
        setPastedImage({ file: namedFile, preview, caption: '' });
        return;
      }
    }
  };

  const sendPastedImage = async () => {
    if (!pastedImage || !activeConversation) return;
    const pasteConvId = await ensureConversation();
    if (!pasteConvId) return;
    try {
      const formData = new FormData();
      formData.append('file', pastedImage.file, `screenshot_${Date.now()}.png`);
      formData.append('conversation_id', pasteConvId);
      const { data } = await axios.post(`${API_URL}/files/upload`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      const s = getSocket();
      if (s) {
        s.emit('send_message', {
          conversation_id: pasteConvId,
          message_type: 'image',
          content: pastedImage.caption.trim() ? pastedImage.caption.trim() : null,
          file_url: data.file_url,
          file_name: data.file_name,
          file_size: data.file_size,
          file_mime_type: data.file_mime_type
        });
      }
      toast.success('📋 Screenshot sent!');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed to send screenshot'); }
    finally { setPastedImage(null); }
  };

  const handleTyping = (e) => {
    const val = e.target.value;
    setMessageInput(val);
    const s = getSocket();
    if (!s || !activeConversation) return;
    s.emit('typing_start', { conversation_id: activeConversation.id });
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => s.emit('typing_stop', { conversation_id: activeConversation.id }), 3000);
    // Detect @ mention
    const cursor = e.target.selectionStart;
    const textUpToCursor = val.slice(0, cursor);
    const atMatch = textUpToCursor.match(/@(\w*)$/);
    if (atMatch && activeConversation?.type === 'group') {
      setMentionQuery(atMatch[1]);
      setShowMentions(true);
      setMentionIndex(0);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  const insertMention = (member) => {
    const cursor = textareaRef.current?.selectionStart || messageInput.length;
    const textUpToCursor = messageInput.slice(0, cursor);
    const atIdx = textUpToCursor.lastIndexOf('@');
    const before = messageInput.slice(0, atIdx);
    const after = messageInput.slice(cursor);
    const newText = `${before}@${member.full_name} ${after}`;
    setMessageInput(newText);
    setShowMentions(false);
    setMentionQuery('');
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = atIdx + member.full_name.length + 2;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 10);
  };

  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef(null);
  // Auto-grow the message box with content (like WhatsApp/Teams), capped at 120px
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }, [messageInput]);
  // Auto-focus the message box when opening a chat or starting a reply
  useEffect(() => {
    if (activeConversation) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [activeConversation, replyTo]);
  const filteredMentions = mentionMembers.filter(m =>
    m.id !== user?.id &&
    (mentionQuery === '' || m.full_name.toLowerCase().includes(mentionQuery.toLowerCase()))
  ).slice(0, 6);

  const [captionMentionQuery, setCaptionMentionQuery] = useState(null);
  const insertCaptionMention = (member) => {
    const cap = pastedImage?.caption || '';
    const atIdx = cap.lastIndexOf('@');
    if (atIdx < 0) return;
    setPastedImage(prev => ({ ...prev, caption: `${cap.slice(0, atIdx)}@${member.full_name} ` }));
    setCaptionMentionQuery(null);
  };

  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileCaptions, setFileCaptions] = useState({});
  const [pendingIndex, setPendingIndex] = useState(0);
  const [fileMentionQuery, setFileMentionQuery] = useState(null);
  const closePendingFiles = () => { setPendingFiles([]); setFileCaptions({}); setPendingIndex(0); setFileMentionQuery(null); };
  // Memoized so typing a caption doesn't spawn a new blob URL per keystroke
  const pendingPreviews = React.useMemo(
    () => pendingFiles.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null),
    [pendingFiles]
  );
  React.useEffect(() => () => { pendingPreviews.forEach(u => { if (u) URL.revokeObjectURL(u); }); }, [pendingPreviews]);
  const insertFileMention = (member) => {
    const cap = fileCaptions[pendingIndex] || '';
    const atIdx = cap.lastIndexOf('@');
    if (atIdx < 0) return;
    setFileCaptions(prev => ({ ...prev, [pendingIndex]: `${cap.slice(0, atIdx)}@${member.full_name} ` }));
    setFileMentionQuery(null);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !activeConversation) return;
    e.target.value = '';
    // All files go through the preview/confirm flow so each can carry its own caption
    setPendingFiles(files);
    setFileCaptions({});
    setPendingIndex(0);
    setFileMentionQuery(null);
  };

  const confirmSendFile = async () => {
    const files = pendingFiles;
    if (!files.length) return;
    const captions = files.map((_, i) => (fileCaptions[i] || '').trim());
    const convId = await ensureConversation();
    if (!convId) return;
    const replyId = replyTo?.id || null;
    const convName = activeConversation?.name;
    closePendingFiles();
    setReplyTo(null);
    files.forEach((file, idx) => {
      setTimeout(async () => {
        const uploadId = Date.now() + idx;
        addUpload({ id: uploadId, fileName: file.name, progress: 0, conversationId: convId, convName, status: 'uploading' });
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('conversation_id', convId);
          const { data } = await axios.post(`${API_URL}/files/upload`, formData, {
            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (p) => updateUpload(uploadId, { progress: Math.round((p.loaded * 100) / p.total) })
          });
          const s = getSocket();
          if (s) s.emit('send_message', {
            conversation_id: convId,
            message_type: file.type.startsWith('image/') ? 'image' : 'file',
            content: captions[idx] ? encryptMessage(captions[idx]) : null,
            file_url: data.file_url, file_name: data.file_name,
            file_size: data.file_size, file_mime_type: data.file_mime_type,
            reply_to_id: idx === 0 ? replyId : null
          });
          updateUpload(uploadId, { status: 'done', progress: 100 });
          setTimeout(() => removeUpload(uploadId), 3000);
        } catch(e) {
          updateUpload(uploadId, { status: 'error' });
          toast.error(e.response?.data?.message || `Failed to send ${file.name}`);
          setTimeout(() => removeUpload(uploadId), 4000);
        }
      }, idx * 400);
    });
    toast.success(files.length === 1 ? 'File sent!' : `Sending ${files.length} files...`);
  };

  const [reactorsModal, setReactorsModal] = useState(null);
  const showReactors = async (msgId) => {
    try {
      const { data } = await axios.get(`${API_URL}/messages/${msgId}/reactions`, { headers: getAuthHeader() });
      setReactorsModal({ reactions: Array.isArray(data) ? data : [] });
    } catch(e) { toast.error('Failed to load reactions'); }
  };

  const addReaction = async (msgId, emoji) => {
    try {
      const { data } = await axios.post(`${API_URL}/messages/${msgId}/react`, { emoji }, { headers: getAuthHeader() });
      // Update only this message's reactions in place — no full reload, so no flash / scroll jump
      // NOTE: authoritative counts come from the message_reaction socket event.
      // Never increment/decrement here or the two paths double-apply (count 2, 1 DB row).
      setMessages(prev => prev.map(m => {
        if (m.id !== msgId) return m;
        const reactions = Array.isArray(m.reactions) ? m.reactions.map(r => ({ ...r })) : [];
        const idx = reactions.findIndex(r => r.emoji === emoji);
        if (data.action === 'added') {
          if (idx >= 0) reactions[idx].reacted_by_me = true;
          else reactions.push({ emoji, count: 1, reacted_by_me: true });
        } else {
          if (idx >= 0) reactions[idx].reacted_by_me = false;
        }
        return { ...m, reactions: reactions.length ? reactions : null };
      }));
    }
    catch(e) { toast.error('Failed to react'); }
  };

  const doDeleteMessage = async (msgId, forEveryone) => {
    try {
      await axios.delete(`${API_URL}/messages/${msgId}`, { headers: getAuthHeader(), data: { for_everyone: forEveryone } });
      if (forEveryone) {
        // In-place: mark deleted, no refetch (avoids the clear+refetch scroll jump)
        setMessages(prev => (Array.isArray(prev) ? prev : []).map(m =>
          Number(m.id) === Number(msgId)
            ? { ...m, is_deleted: true, content: 'This message was deleted', reactions: null }
            : m
        ));
        if (activeConversation) {
          const last = messages[messages.length - 1];
          if (last && Number(last.id) === Number(msgId)) {
            updateConversation({ id: activeConversation.id, last_message: 'This message was deleted', last_message_deleted: true });
          }
        }
      } else {
        // Delete for me — remove from my view only
        setMessages(prev => (Array.isArray(prev) ? prev : []).filter(m => Number(m.id) !== Number(msgId)));
      }
      setMsgActionMenu(null);
    } catch(e) { toast.error('Failed to delete'); }
  };

  // Confirming wrapper — MessageBubble calls this; shows in-app confirm before deleting.
  const deleteMessage = (msgId, forEveryone) => {
    setMsgActionMenu(null);
    askConfirm({
      title: forEveryone ? 'Delete for everyone' : 'Delete for me',
      message: forEveryone
        ? 'This message will be deleted for everyone in this chat. This cannot be undone.'
        : 'This message will be removed from your view only.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => doDeleteMessage(msgId, forEveryone)
    });
  };
  const forwardMessage = async (msg, targets) => {
    const s = getSocket();
    if (!s) return toast.error('Not connected');
    const msgs = Array.isArray(msg) ? msg : [msg];
    let sent = 0;
    for (const t of targets) {
      let convId = t.convId;
      if (!convId && t.userId) {
        // No existing chat with this user — create/get the direct conversation first
        try {
          const { data } = await axios.post(`${API_URL}/chat/conversations/direct`, { user_id: t.userId }, { headers: getAuthHeader() });
          convId = data.conversation_id;
        } catch(e) { continue; }
      }
      if (!convId) continue;
      const ordered = [...msgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      for (const fm of ordered) {
        s.emit('send_message', {
          conversation_id: convId,
          forward_message_id: fm.id,
          reply_to_id: null,
          is_forwarded: true
        });
      }
      sent++;
    }
    if (sent > 0) fetchConversations(); // surface any newly created chats
    toast.success(`Forwarded ${msgs.length > 1 ? msgs.length + ' messages' : ''} to ${sent} conversation${sent > 1 ? 's' : ''}`);
    exitSelectMode();
  };

  const pinMessage = async (msg) => {
    // Toggle: the menu label already switches to "Unpin", but this always
    // POSTed, so an already-pinned message could never be unpinned.
    const isPinned = !!msg.is_pinned;
    try {
      if (isPinned) {
        await axios.delete(`${API_URL}/messages/${msg.id}/pin`, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API_URL}/messages/${msg.id}/pin`, { conversation_id: activeConversation.id }, { headers: getAuthHeader() });
      }
      toast.success(isPinned ? 'Message unpinned' : 'Message pinned');
      setMsgActionMenu(null);
      // Update in place rather than refetching — a full refetch replaces the
      // message array and throws the scroll position back to the bottom.
      setMessages(prev => (Array.isArray(prev) ? prev : []).map(m =>
        Number(m.id) === Number(msg.id) ? { ...m, is_pinned: !isPinned } : m
      ));
    } catch(e) { toast.error(isPinned ? 'Failed to unpin' : 'Failed to pin'); }
  };

  const startEdit = (msg) => { setEditingMsg(msg); setMessageInput(decryptMessage(msg.content)); setMsgActionMenu(null); };

  const saveEdit = async () => {
    if (!editingMsg || !messageInput.trim()) return;
    try {
      await axios.put(`${API_URL}/messages/${editingMsg.id}`, { content: encryptMessage(messageInput) }, { headers: getAuthHeader() });
      // No refetch — the message_edited socket echo updates in place (keeps scroll position)
      setEditingMsg(null); setMessageInput('');
    } catch(e) { toast.error('Failed to edit'); }
  };

  const saveProfile = async () => {
    try {
      const { data } = await axios.put(`${API_URL}/users/me`, profileForm, { headers: getAuthHeader() });
      if (avatarFile) {
        const fd = new FormData(); fd.append('avatar', avatarFile);
        await axios.post(`${API_URL}/users/me/avatar`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      }
      setUser({ ...user, ...data.user }); toast.success('Profile updated!'); setEditingProfile(false);
    } catch(e) { toast.error('Failed to update'); }
  };

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/meetings`, { ...meetingForm, conversation_id: activeConversation?.id }, { headers: getAuthHeader() });
      toast.success('Meeting invite sent!'); setShowMeetingModal(false);
      setMeetingForm({ title:'', description:'', meet_link:'', scheduled_at:'', duration_minutes:30, participant_ids:[] });
    } catch(e) { toast.error('Failed to create meeting'); }
  };

  const respondMeeting = async (id, status) => {
    try {
      await axios.put(`${API_URL}/meetings/${id}/respond`, { status }, { headers: getAuthHeader() });
      toast.success(status === 'accepted' ? '✓ Meeting accepted' : '✗ Meeting declined');
      // Optimistically update local state immediately
      setMeetings(prev => prev.map(m => m.id === id ? { ...m, my_status: status } : m));
      // Then refresh from server
      fetchMeetings();
    } catch(e) { toast.error('Failed to respond'); }
  };

  // Sidebar timestamp, WhatsApp-style: time today, "Yesterday", weekday within
  // the last week, then the date. Previously every row showed HH:mm, so a
  // message from last week looked like it arrived today.
  const getConvTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    if (isToday(d)) return format(d, 'HH:mm');
    if (isYesterday(d)) return 'Yesterday';
    const days = (Date.now() - d.getTime()) / 86400000;
    if (days < 7) return format(d, 'EEE');
    return format(d, 'dd/MM/yy');
  };

  const getDateLabel = (date) => {
    if (isToday(new Date(date))) return 'Today';
    if (isYesterday(new Date(date))) return 'Yesterday';
    return format(new Date(date), 'MMMM d, yyyy');
  };

  const isOnline = (userId) => {
    const id = Number(userId);
    if (!onlineUsers[id] && !onlineUsers[userId]) return false;
    // Check status from userStatuses store (updated via socket)
    const status = userStatuses[id] || userStatuses[userId]
      // Fallback: check users list for their saved status
      || users.find(u => Number(u.id) === id)?.status
      // Fallback: check if it's current user
      || (Number(user?.id) === id ? user?.status : null);
    if (status && ['away','dnd','meeting','lunch','vacation'].includes(status)) return false;
    return true;
  };
  const typingInConv = activeConversation && typingUsers[activeConversation.id] ? typingUsers[activeConversation.id] : null;
  const filteredConversations = conversations.filter(c => (c.name||'').toLowerCase().includes(search.toLowerCase()));
  const filteredUsers = users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  const GroupAvatar = ({ name, size=11 }) => {
    const colors = [
      'from-purple-500 to-pink-500',
      'from-orange-500 to-red-500', 
      'from-green-500 to-teal-500',
      'from-blue-500 to-indigo-500',
      'from-yellow-500 to-orange-500',
    ];
    const colorIdx = name ? name.charCodeAt(0) % colors.length : 0;
    return (
      <div className="relative flex-shrink-0">
        <div className={`rounded-2xl bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-white shadow-md`}
          style={{width:`${size*4}px`, height:`${size*4}px`}}>
          {/* Group icon with initials */}
          <div className="flex flex-col items-center gap-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{width:`${size*1.8}px`, height:`${size*1.8}px`, opacity:0.9}}>
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
            </svg>
          </div>
        </div>
      </div>
    );
  };

;

  const Avatar = ({ name, url, size=10, online=false, isGroup=false }) => {
    const getAvatarSrc = (url) => {
      if (!url) return null;
      if (url.startsWith('https://')) return url; // S3 URL
      if (url.startsWith('/uploads/')) return `https://qtalk.qualtechedge.in${url}`; // legacy local
      return null;
    };
    const avatarSrc = getAvatarSrc(url);
    return (
      <div className="relative flex-shrink-0">
        <div className="rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold overflow-hidden"
          style={{width:`${size*4}px`, height:`${size*4}px`}}>
          {avatarSrc
            ? <img src={avatarSrc} className="w-full h-full object-cover" alt=""/>
            : isGroup
            ? <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:`${size*2.2}px`,height:`${size*2.2}px`}}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            : <span style={{fontSize:`${size*1.5}px`, lineHeight:1}}>{name ? name.trim().split(/\s+/).slice(0,2).map(w=>w[0].toUpperCase()).join('') : '?'}</span>}
        </div>
        {online && <span className="absolute bottom-0 right-0 bg-green-400 rounded-full border-2 border-white dark:border-gray-800" style={{width:'12px',height:'12px'}}/>}
      </div>
    );
  };

  if (isMobile) return <MobileChatPage />;

  return (
    <div className={`flex h-screen overflow-hidden ${darkMode ? 'dark' : ''}`}>
      {showDisconnBanner && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white text-sm font-medium py-2 px-4 flex items-center justify-center gap-3 shadow-lg">
          <span>⚠️ Connection lost — trying to reconnect…</span>
          <button onClick={() => window.location.reload()}
            className="px-3 py-0.5 bg-white/25 hover:bg-white/35 rounded-lg text-xs font-semibold transition">
            Refresh
          </button>
        </div>
      )}

      {/* LEFT RAIL */}
      <div className="flex-shrink-0 bg-gradient-to-b from-teal-600 to-blue-700 flex flex-col items-center py-3 gap-0.5" style={{width:'60px'}}>
        <div className="mb-2 cursor-pointer" onClick={() => setShowPanel(null)}>
          <img src="/logo.png" alt="Qtalk" className="object-contain" style={{width:'40px',height:'40px',filter:'brightness(0) invert(1)'}}/>
        </div>
        <div className="w-8 h-px bg-white/20 mb-1"/>
        {[
          { id:'chats', label:'Chats', svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
          { id:'people', label:'People', svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg> },

          { id:'calls', label:'Calls', svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg> },
          { id:'feed', label:'Feed', svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>, badge: unreadAnnouncements },
          { id:'tasks', label:'Tasks', svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
          { id:'calendar', label:'Calendar', svg:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14" strokeWidth="3"/><line x1="12" y1="14" x2="12" y2="14" strokeWidth="3"/></svg> },
          { id:'notes', label:'Notes', svg:<svg viewBox="0 0 26 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="2" y="2" width="13" height="18" rx="2"/><line x1="5" y1="2" x2="5" y2="20"/><circle cx="4" cy="6" r="0.8" fill="currentColor" stroke="none"/><circle cx="4" cy="10" r="0.8" fill="currentColor" stroke="none"/><circle cx="4" cy="14" r="0.8" fill="currentColor" stroke="none"/><line x1="8" y1="7" x2="12" y2="7"/><line x1="8" y1="11" x2="12" y2="11"/><line x1="8" y1="15" x2="11" y2="15"/><path d="M15 14l5-5 2 2-5 5h-2v-2z"/><path d="M19 10l2 2"/></svg> },
        ].map(tab => (
          <button key={tab.id} onClick={() => {
            if (tab.id === 'feed') { setUnreadAnnouncements(0); window.location.href='/announcements'; return; }
            if (tab.id === 'tasks') { window.location.href='/todos'; return; }
            if (tab.id === 'calendar') { window.location.href='/calendar'; return; }
            if (tab.id === 'notes') { window.location.href='/notes'; return; }
            if (tab.id === 'meetings') { window.location.href='/calendar'; return; }
            setActiveTab(tab.id); setShowPanel('sidebar'); setShowUpcoming(false); setSearch('');
            if (tab.id === 'calls') { setMissedCalls(0); fetchCallHistory(true); }
          }} title={tab.label}
            className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all relative
              ${activeTab===tab.id && showPanel==='sidebar' ? 'bg-white/25 text-white' : 'text-white/60 hover:bg-white/15 hover:text-white'}`}>
            {tab.svg}
            <span className="text-white/80 font-medium" style={{fontSize:'8px'}}>{tab.label}</span>
            {tab.id==='meetings' && meetings.filter(m => m.my_status === 'pending' && !m.is_organizer).length > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-400 rounded-full"/>
            )}
            {tab.id==='calls' && missedCalls > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 text-[8px] font-bold flex items-center justify-center bg-red-500 text-white rounded-full">
                {missedCalls > 9 ? '9+' : missedCalls}
              </span>
            )}
            {tab.id==='feed' && unreadAnnouncements > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 text-[8px] font-bold flex items-center justify-center bg-red-500 text-white rounded-full">
                {unreadAnnouncements > 9 ? '9+' : unreadAnnouncements}
              </span>
            )}
          </button>
        ))}
        <div className="flex-1"/>
        <div className="w-8 h-px bg-white/20 mb-1"/>

        {/* ── BOTTOM RAIL: Notifs → Settings → Admin → Profile ── */}

        {/* Notifications */}
        <button onClick={() => { setShowNotifPanel(p => !p); markAllNotifsRead(); }} title="Notifications"
          className="relative w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 text-white/60 hover:bg-white/15 hover:text-white transition-all">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
          </svg>
          {unreadNotifs > 0 && (
            <span className="absolute top-1 right-1 min-w-[14px] h-3.5 px-0.5 text-[8px] font-bold flex items-center justify-center bg-red-500 text-white rounded-full leading-none">
              {unreadNotifs > 9 ? '9+' : unreadNotifs}
            </span>
          )}
          <span className="text-white/80 font-medium" style={{fontSize:'8px'}}>Notifs</span>
        </button>

        {/* Settings */}
        <button onClick={() => setShowSettings(true)} title="Settings"
          className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all ${showSettings ? 'bg-white/25 text-white' : 'text-white/60 hover:bg-white/15 hover:text-white'}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
          <span className="text-white/80 font-medium" style={{fontSize:'8px'}}>Settings</span>
        </button>

        {/* Admin — temporary */}
        {user?.is_admin && (
          <a href="/admin" title="Admin Panel"
            className="w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 text-white/60 hover:bg-white/15 hover:text-white transition-all">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span className="text-white/80 font-medium" style={{fontSize:'8px'}}>Admin</span>
          </a>
        )}

        {/* Profile avatar — VERY LAST at bottom */}
        <div className="relative group mb-1 mt-0.5">
          <button
            onClick={() => setShowPanel(showPanel==='profile' ? null : 'profile')}
            title={user?.full_name || 'Profile'}
            className={`w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center transition-all ${showPanel==='profile' ? 'ring-2 ring-white' : 'ring-2 ring-transparent hover:ring-2 hover:ring-white/60'}`}
            style={{flexShrink:0}}>
            {(() => {
              const src = avatarPreview || (user?.avatar_url ? (user.avatar_url.startsWith('https://') ? user.avatar_url : `https://qtalk.qualtechedge.in${user.avatar_url}`) : null);
              return src
                ? <img src={src} className="w-full h-full object-cover" alt="me" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}/>
                : null;
            })()}
            <div className="w-full h-full items-center justify-center text-white font-bold text-sm"
              style={{background:'linear-gradient(135deg,#0d9488,#2563eb)', display:(avatarPreview || user?.avatar_url) ? 'none' : 'flex'}}>
              {user?.full_name?.[0]?.toUpperCase() || '?'}
            </div>
          </button>
          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full pointer-events-none"
            style={{border:'2px solid #0f766e'}}/>
          {showPanel !== 'profile' && (
            <div className="absolute left-12 bottom-1 hidden group-hover:flex flex-col bg-gray-900/95 text-white rounded-xl shadow-2xl px-3 py-2.5 z-50 whitespace-nowrap pointer-events-none"
              style={{minWidth:160,backdropFilter:'blur(8px)'}}>
              <span className="font-bold text-xs">{user?.full_name}</span>
              <span className="text-xs mt-0.5" style={{color:'#4ade80'}}>● Online</span>
              <span className="text-gray-400 text-xs mt-1.5 border-t border-white/10 pt-1.5">View my profile</span>
            </div>
          )}
        </div>

      </div>

      {/* Upcoming meetings popup */}
      {showUpcoming && (
        <div className="fixed inset-0 z-40" onClick={() => setShowUpcoming(false)}/>
      )}
      {showUpcoming && (
        <div className="fixed left-20 bottom-16 w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50"
          style={{maxHeight:'480px'}}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700"
            style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span className="text-white font-semibold text-sm">Upcoming Meetings</span>
            </div>
            <button onClick={() => setShowUpcoming(false)} className="text-white/70 hover:text-white transition">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div className="overflow-y-auto" style={{maxHeight:'340px'}}>
            {meetings.filter(m => new Date(m.scheduled_at) >= new Date() && m.my_status !== 'declined').sort((a,b) => new Date(a.scheduled_at)-new Date(b.scheduled_at)).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10 mb-2 opacity-30">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <p className="text-sm">No upcoming meetings</p>
              </div>
            ) : (
              meetings.filter(m => new Date(m.scheduled_at) >= new Date() && m.my_status !== 'declined').sort((a,b) => new Date(a.scheduled_at)-new Date(b.scheduled_at)).map(m => {
                const meetDate = new Date(m.scheduled_at);
                const isToday = new Date().toDateString() === meetDate.toDateString();
                return (
                  <div key={m.id} className="px-4 py-3 border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex flex-col items-center justify-center text-white text-xs font-bold ${isToday ? 'bg-teal-500' : 'bg-blue-500'}`}>
                        <span style={{fontSize:'10px'}}>{meetDate.toLocaleString('en',{month:'short'}).toUpperCase()}</span>
                        <span className="text-base leading-none">{meetDate.getDate()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{m.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{format(meetDate,'HH:mm')} · {m.duration_minutes} min</p>
                        <p className="text-xs text-gray-400 truncate">{m.is_organizer ? 'You organized' : `By ${m.organizer_name}`}</p>
                        {m.meet_link && (
                          <a href={m.meet_link.startsWith('http') ? m.meet_link : `https://qtalk.qualtechedge.in${m.meet_link}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-1 text-xs text-teal-500 hover:text-teal-600 font-medium transition">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                            Join
                          </a>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${m.my_status==='accepted'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>
                        {m.my_status==='accepted'?'✓':'●'}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex gap-2">
            <button onClick={() => { setShowUpcoming(false); setActiveTab('meetings'); setShowPanel('sidebar'); }}
              className="flex-1 py-2 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 rounded-xl hover:bg-teal-100 transition">
              Full Calendar
            </button>
            <button onClick={() => {
                setShowUpcoming(false);
                const mid = generateMeetId();
                setMeetingForm(f=>({...f, meet_link:`https://qtalk.qualtechedge.in/meet/${mid}`, _meetId:mid}));
                setShowMeetingModal(true);
              }}
              className="flex-1 py-2 text-xs font-semibold text-white bg-gradient-to-r from-teal-500 to-blue-500 rounded-xl hover:opacity-90 transition">
              + Schedule
            </button>
          </div>
        </div>
      )}

      {/* SIDE PANEL */}
      {showPanel && (
        <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {showPanel !== 'profile' && (
            <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800 dark:text-white text-lg">
                  {activeTab==='chats' ? '💬 Chats' : activeTab==='people' ? '👥 People' : activeTab==='calls' ? '📞 Calls' : (
                    <span className="flex items-center gap-2">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-teal-500"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                      Meetings
                    </span>
                  )}
                </h2>
                {activeTab==='chats' && showPanel==='sidebar' && (
                  <div className="flex gap-1">
                    <button onClick={() => setShowGroupModal(true)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition" title="New group">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                        <line x1="20" y1="8" x2="20" y2="14"/><line x1="17" y1="11" x2="23" y2="11"/>
                      </svg>
                    </button>
                    <button onClick={() => { setShowPanel('newchat'); setNewChatSearch(''); }}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition" title="New chat">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M11 4H4a2 2 0 00-2 2v14l4-4h14a2 2 0 002-2v-5"/><path d="M18 2l4 4-8 8H10v-4z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              {showPanel !== 'newchat' && (
                <div className="relative">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {/* PROFILE */}
            {showPanel==='profile' && (
              <div className="flex flex-col h-full overflow-y-auto bg-gray-50 dark:bg-gray-950">

                {/* ── HERO ── */}
                <div className="relative" style={{background:'linear-gradient(135deg,#0d9488 0%,#0369a1 60%,#1d4ed8 100%)'}}>
                  <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)',backgroundSize:'28px 28px'}}/>
                  <div className="relative px-4 py-4 flex items-center gap-3">
                    {/* Avatar compact */}
                    <div className="relative flex-shrink-0">
                      <div onClick={() => setShowAvatarMenu(p => !p)}
                        className="rounded-2xl overflow-hidden cursor-pointer shadow-xl border-2 border-white/30 hover:border-white/60 transition-all"
                        style={{width:'62px',height:'62px'}}>
                        {(avatarPreview || user?.avatar_url)
                          ? <img src={avatarPreview || (user.avatar_url?.startsWith('https://')?user.avatar_url:`https://qtalk.qualtechedge.in${user.avatar_url}`)} className="w-full h-full object-cover" alt="avatar"/>
                          : <div className="w-full h-full flex items-center justify-center" style={{background:'rgba(255,255,255,0.2)'}}>
                              <span className="text-2xl font-black text-white">{user?.full_name?.[0]?.toUpperCase()}</span>
                            </div>}
                      </div>
                      <span className="absolute top-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-md"/>
                      {/* Avatar menu */}
                      {showAvatarMenu && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowAvatarMenu(false)}/>
                          <div className="absolute top-full left-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 pt-3 pb-2">Profile Photo</p>
                            {(user?.avatar_url || avatarPreview) && (
                              <button onClick={() => { setLightbox(avatarPreview || (user.avatar_url?.startsWith('https://')?user.avatar_url:`https://qtalk.qualtechedge.in${user.avatar_url}`)); setZoomLevel(1); setShowAvatarMenu(false); }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                                <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-blue-500"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                </div>
                                View photo
                              </button>
                            )}
                            <label className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer">
                              <div className="w-8 h-8 rounded-xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-teal-500"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                              </div>
                              Change photo
                              <input type="file" accept="image/*" className="hidden" onChange={e => {
                                const f = e.target.files[0];
                                if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); setShowAvatarMenu(false); if (!editingProfile) setEditingProfile(true); }
                              }}/>
                            </label>
                            {(user?.avatar_url || avatarPreview) && (
                              <button onClick={removeAvatar}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition border-t border-gray-100 dark:border-gray-700">
                                <div className="w-8 h-8 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-red-400"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                                </div>
                                Remove photo
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {/* Info beside avatar */}
                    <div className="flex-1 min-w-0">
                      <h2 className="text-sm font-black text-white tracking-tight truncate">{user?.full_name}</h2>
                      <p className="text-white/60 text-xs truncate mt-0.5">{user?.email}</p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {profileForm.designation && (
                          <span className="text-xs font-semibold bg-white/15 text-white px-2 py-0.5 rounded-full border border-white/20">{profileForm.designation}</span>
                        )}
                        {profileForm.department && (
                          <span className="text-xs font-semibold bg-white/15 text-white px-2 py-0.5 rounded-full border border-white/20">{profileForm.department}</span>
                        )}
                      </div>
                      {profileForm.status && (
                        <p className="text-white/50 text-xs mt-1 italic truncate">"{profileForm.status}"</p>
                      )}
                    </div>
                  </div>
                </div>
                {/* ── BODY ── */}
                <div className="px-4 pb-6 pt-3">
                  {!editingProfile ? (
                    <div className="space-y-3">

                      {/* Quick actions — compact */}
                      <button onClick={() => setEditingProfile(true)}
                        className="w-full flex items-center justify-center gap-2 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-semibold text-gray-600 dark:text-gray-300 hover:border-teal-400 hover:text-teal-600 transition shadow-sm">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Edit Profile
                      </button>

                      {/* Status selector - compact button with popup */}
                      <div className="relative">
                        <button onClick={() => setShowStatusMenu(!showStatusMenu)}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-teal-400 transition shadow-sm">
                          <span className="text-sm">
                            {currentStatus==='online'?'🟢':currentStatus==='away'?'🟡':currentStatus==='dnd'?'🔴':currentStatus==='meeting'?'🔵':currentStatus==='lunch'?'🍽️':currentStatus==='vacation'?'🏖️':'💬'}
                          </span>
                          <span className="flex-1 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 capitalize truncate">
                            {currentStatus==='dnd'?'Do Not Disturb':currentStatus==='meeting'?'In Meeting':currentStatus==='lunch'?'At Lunch':currentStatus}
                          </span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-gray-400">
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        </button>
                        {showStatusMenu && (
                          <div className="absolute top-10 left-0 right-0 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-50 overflow-hidden">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-3 pt-3 pb-1.5">Set Status</p>
                            {[
                              {id:'online',icon:'🟢',label:'Online'},
                              {id:'away',icon:'🟡',label:'Away'},
                              {id:'dnd',icon:'🔴',label:'Do Not Disturb'},
                              {id:'meeting',icon:'🔵',label:'In Meeting'},
                              {id:'lunch',icon:'🍽️',label:'At Lunch'},
                              {id:'vacation',icon:'🏖️',label:'Vacation'},
                            ].map(s => (
                              <button key={s.id} onClick={() => { setCustomStatus(s.id); setShowStatusMenu(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-semibold transition hover:bg-gray-50 dark:hover:bg-gray-700
                                  ${currentStatus===s.id ? 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20' : 'text-gray-700 dark:text-gray-200'}`}>
                                <span className="text-sm">{s.icon}</span>
                                <span className="flex-1 text-left">{s.label}</span>
                                {currentStatus===s.id && <span className="text-teal-500">✓</span>}
                              </button>
                            ))}
                            <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700">
                              <input type="text" placeholder="Custom status..."
                                onKeyDown={e => { if(e.key==='Enter'&&e.target.value.trim()){setCustomStatus(e.target.value.trim());e.target.value='';setShowStatusMenu(false);}}}
                                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400"/>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Info section */}
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 pt-3 pb-2">Contact Info</p>
                        {[
                          {color:'#0d9488',icon:<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>, label:'Mobile', val:profileForm.mobile},
                          {color:'#3b82f6',icon:<><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>, label:'Department', val:profileForm.department},
                          {color:'#8b5cf6',icon:<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></>, label:'Designation', val:profileForm.designation},
                        ].map((item, i, arr) => (
                          <div key={item.label} className={`flex items-center gap-3 px-4 py-3 ${i < arr.length-1 ? 'border-b border-gray-50 dark:border-gray-700' : ''}`}>
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:`${item.color}15`}}>
                              <svg viewBox="0 0 24 24" fill="none" stroke={item.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                                {item.icon}
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                              <p className={`text-sm font-semibold truncate ${item.val ? 'text-gray-800 dark:text-gray-100' : 'text-gray-300 dark:text-gray-600 italic font-normal'}`}>
                                {item.val || 'Not set'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Meeting room */}
                      {user?.meet_link && (
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 pt-3 pb-2">Your Meeting Room</p>
                          <div className="px-4 pb-3 flex items-center gap-2">
                            <code className="flex-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-xl truncate font-mono">/meet/{user.meet_link}</code>
                            <button onClick={() => { navigator.clipboard.writeText(`https://qtalk.qualtechedge.in/meet/${user.meet_link}`); toast.success('Link copied!'); }}
                              className="flex-shrink-0 w-9 h-9 bg-teal-500 hover:bg-teal-600 text-white rounded-xl flex items-center justify-center transition shadow-sm">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                            </button>
                          </div>
                        </div>
                      )}



                      {/* Logout button — bottom of profile */}
                      <button onClick={logout}
                        className="w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl border border-red-100 dark:border-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition font-semibold text-sm group">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 transition group-hover:translate-x-0.5">
                          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                          <polyline points="16 17 21 12 16 7"/>
                          <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        Sign out of Qtalk
                      </button>

                    </div>
                  ) : (
                    // ── EDIT MODE ──
                    <div className="space-y-2">
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 pt-3 pb-1">Edit Profile</p>
                        {[
                          {key:'full_name', label:'Full Name', type:'text', placeholder:'Your full name', icon:'#0d9488'},
                          {key:'mobile', label:'Mobile *', type:'tel', placeholder:'+91 98765 43210', icon:'#3b82f6'},
                          {key:'department', label:'Department', type:'text', placeholder:'e.g. Infrastructure', icon:'#8b5cf6'},
                          {key:'designation', label:'Designation', type:'text', placeholder:'e.g. Senior Engineer', icon:'#f59e0b'},
                          {key:'status', label:'Status Message', type:'text', placeholder:'Hey, I am on Qtalk!', icon:'#10b981'},
                        ].map((field, i, arr) => (
                          <div key={field.key} className={`px-4 py-3 ${i < arr.length-1 ? 'border-b border-gray-50 dark:border-gray-700' : ''}`}>
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-1.5">{field.label}</label>
                            <input type={field.type} placeholder={field.placeholder}
                              value={profileForm[field.key]} onChange={e => setProfileForm({...profileForm,[field.key]:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl text-sm text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 border border-transparent focus:border-teal-400 focus:bg-white dark:focus:bg-gray-600 focus:outline-none transition placeholder-gray-300 dark:placeholder-gray-500 font-medium"/>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => setEditingProfile(false)}
                          className="flex-1 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-2xl text-sm font-semibold hover:bg-gray-50 transition">
                          Cancel
                        </button>
                        <button onClick={saveProfile}
                          className="flex-1 py-3 text-white font-bold rounded-2xl text-sm transition hover:opacity-90 shadow-lg"
                          style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* NEW CHAT */}
            {showPanel==='newchat' && (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-800 dark:text-white text-sm">New Conversation</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{users.length} people in your workspace</p>
                    </div>
                    <button onClick={() => setShowPanel('sidebar')}
                      className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-gray-600 transition">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                  {/* Search box */}
                  <div className="relative">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search by name, email or department..."
                      value={newChatSearch}
                      onChange={e => setNewChatSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 text-xs rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
                    {newChatSearch && (
                      <button onClick={() => setNewChatSearch('')}
                        className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* User list */}
                <div className="flex-1 overflow-y-auto py-2">
                  {/* Message yourself — pinned */}
                  {!newChatSearch && (
                    <div onClick={() => { startChat(user?.id); setNewChatSearch(''); }}
                      className="flex items-center gap-3 px-4 py-2.5 mb-1 hover:bg-teal-50 dark:hover:bg-teal-900/20 cursor-pointer transition border-b border-gray-100 dark:border-gray-700">
                      <Avatar name={user?.full_name} url={user?.avatar_url} size={10}/>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{user?.full_name} <span className="text-teal-600 dark:text-teal-400 font-medium">(You)</span></p>
                        <p className="text-xs text-gray-400">Message yourself · Save notes and files</p>
                      </div>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-teal-500 flex-shrink-0"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    </div>
                  )}
                  {(() => {
                    const filtered = users.filter(u =>
                      u.full_name.toLowerCase().includes(newChatSearch.toLowerCase()) ||
                      u.email.toLowerCase().includes(newChatSearch.toLowerCase()) ||
                      (u.department||'').toLowerCase().includes(newChatSearch.toLowerCase()) ||
                      (u.designation||'').toLowerCase().includes(newChatSearch.toLowerCase())
                    );
                    const online = filtered.filter(u => isOnline(u.id));
                    const offline = filtered.filter(u => !isOnline(u.id));

                    if (filtered.length === 0) return (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10 mb-2 opacity-30">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <p className="text-sm font-medium">No results for "{newChatSearch}"</p>
                        <p className="text-xs mt-1">Try searching by name or department</p>
                      </div>
                    );

                    const UserRow = ({ u }) => (
                      <div key={u.id} onClick={() => { startChat(u.id); setNewChatSearch(''); }}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition group">
                        <Avatar name={u.full_name} url={u.avatar_url} size={10} online={isOnline(u.id)}/>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{u.full_name}</p>
                          <p className="text-xs text-gray-400 truncate">{u.designation ? `${u.designation}${u.department ? ` · ${u.department}` : ''}` : u.email}</p>
                        </div>
                        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            className="w-4 h-4 text-teal-500">
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                          </svg>
                        </div>
                      </div>
                    );

                    return (
                      <>
                        {online.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-4 py-1.5">
                              Online — {online.length}
                            </p>
                            {online.map(u => <UserRow key={u.id} u={u}/>)}
                          </>
                        )}
                        {offline.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-4 py-1.5 mt-1">
                              {online.length > 0 ? `Offline — ${offline.length}` : `All users — ${offline.length}`}
                            </p>
                            {offline.map(u => <UserRow key={u.id} u={u}/>)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Footer — new group shortcut */}
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                  <button onClick={() => { setShowGroupModal(true); setShowPanel('sidebar'); }}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-teal-400 hover:text-teal-500 transition text-xs font-semibold">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                      <line x1="20" y1="8" x2="20" y2="14"/><line x1="17" y1="11" x2="23" y2="11"/>
                    </svg>
                    Create a group instead
                  </button>
                </div>
              </div>
            )}

            {/* CHATS */}
            {showPanel==='sidebar' && activeTab==='chats' && (
              <div>
                {filteredConversations.length===0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <span className="text-4xl mb-2">💬</span>
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs mt-1">Click ✏️ to start chatting</p>
                  </div>
                )}
                {filteredConversations.map(conv => (
                  <div key={conv.id} className={`group relative flex items-center gap-3 px-3 py-3 cursor-pointer transition border-b border-gray-50 dark:border-gray-800
                    ${activeConversation?.id===conv.id ? 'bg-teal-50 dark:bg-teal-900/20 border-l-4 border-l-teal-500' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    onClick={() => { activeConvRef.current = conv; setActiveConversation(conv); setShowProfilePanel(false); setProfilePanelUser(null); }}>
                    <Avatar name={conv.name} url={conv.avatar_url} size={11} isGroup={conv.type==='group'} online={conv.type!=='group' && conv.other_user && isOnline(conv.other_user.id)}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate flex items-center gap-1">
                          {conv.is_pinned && <span className="text-xs flex-shrink-0" title="Pinned">📌</span>}
                          <span className="truncate">{conv.name||'Chat'}</span>
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {conv.last_message_time && <span className="text-xs text-gray-400 flex-shrink-0">{getConvTime(conv.last_message_time)}</span>}
                          {/* WhatsApp-style chevron menu on hover */}
                          <div className="relative opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={e => { e.stopPropagation(); setConvMenuOpen(convMenuOpen===conv.id ? null : conv.id); }}
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 transition">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M7 10l5 5 5-5z"/></svg>
                            </button>
                            {convMenuOpen===conv.id && (
                              <div className="absolute right-0 top-7 w-52 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/60 z-50 overflow-hidden py-1 animate-[fadeIn_0.12s_ease-out]"
                                onMouseLeave={() => setConvMenuOpen(null)}>
                                {[
                                  { label: conv.is_pinned ? 'Unpin chat' : 'Pin chat', icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14l-1.5-3V7a2 2 0 00-2-2h-7a2 2 0 00-2 2v7L5 17z"/></svg>), action: async () => {
                                    setConvMenuOpen(null);
                                    try {
                                      if (conv.is_pinned) {
                                        await axios.delete(`${API_URL}/chat/conversations/${conv.id}/pin`, { headers: getAuthHeader() });
                                        toast.success('Unpinned');
                                      } else {
                                        await axios.post(`${API_URL}/chat/conversations/${conv.id}/pin`, {}, { headers: getAuthHeader() });
                                        toast.success('Pinned');
                                      }
                                      fetchConversations();
                                    } catch(e) { toast.error('Failed'); }
                                  }},
                                  { label: 'Mute', icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><path d="M13.73 21a2 2 0 01-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0118 8"/><path d="M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 00-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></svg>), action: () => { toast('Coming soon'); setConvMenuOpen(null); }},
                                  { label: 'Mark as read', icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>), action: () => {
                                    setUnreadCounts(p=>({...p,[conv.id]:0}));
                                    const s = getSocket();
                                    if (s) s.emit('mark_read', { conversation_id: conv.id });
                                    setConvMenuOpen(null);
                                  }},
                                  { label: 'Delete chat', icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>), action: () => {
                                    const delId = Number(conv.id);
                                    const convName = conv.name || 'this chat';
                                    setConvMenuOpen(null);
                                    askConfirm({
                                      title: 'Delete chat',
                                      message: `Remove "${convName}" from your chat list?`,
                                      confirmLabel: 'Delete',
                                      danger: true,
                                      onConfirm: async () => {
                                        removeConversation(delId);
                                        if (activeConversation && Number(activeConversation.id) === delId) {
                                          setActiveConversation(null);
                                          setMessages([]);
                                        }
                                        try {
                                          await axios.delete(`${API_URL}/chat/conversations/${delId}/hide`, { headers: getAuthHeader() });
                                        } catch(e) {}
                                        toast.success('Chat removed');
                                      }
                                    });
                                  }, danger: true },
                                ].map((item,i) => (
                                  <button key={i} onClick={item.action}
                                    className={`w-full text-left px-4 py-2.5 text-sm font-medium transition flex items-center gap-2.5 ${item.danger?'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20':'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60'}`}>
                                    {item.icon}
                                    <span>{item.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          {/* Tick marks for sent messages */}
                          {(Number(conv.last_sender_id) === Number(user?.id) || conv.last_message_is_mine) && (
                            <span className="flex-shrink-0">
                              {conv.last_message_read_by_others ? (
                                /* Blue double tick - read */
                                <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                                  <path d="M1 5.5L4.5 9L10 3" stroke="#17C3B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M6 5.5L9.5 9L15 3" stroke="#17C3B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              ) : (
                                /* Grey double tick - delivered */
                                <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                                  <path d="M1 5.5L4.5 9L10 3" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M6 5.5L9.5 9L15 3" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </span>
                          )}
                          <p className="text-xs text-gray-400 truncate flex-1 flex items-center gap-1">
                            {/* Group: show sender name */}
                            {conv.type==='group' && conv.last_sender_name && (
                              <span className="font-semibold text-teal-500 flex-shrink-0" style={{fontSize:'11px'}}>
                                {conv.last_message_is_mine ? 'You:' : `${conv.last_sender_name.split(' ')[0]}:`}
                              </span>
                            )}
                            {typingUsers[conv.id] ? (
                              <span className="text-teal-500 animate-pulse" style={{fontSize:'11px'}}>✍️ {typingUsers[conv.id].name?.split(' ')[0]} is typing...</span>
                            ) : conv.last_message_deleted ? (
                              <span className="italic opacity-70">🚫 This message was deleted</span>
                            ) : conv.last_message_type==='file' ? (
                              <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px',flexShrink:0}}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg><span>File</span></>
                            ) : conv.last_message_type==='image' ? (
                              <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px',flexShrink:0}}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span>Image</span></>
                            ) : conv.last_message ? (() => { let t = decryptMessage(conv.last_message); t = (t || '').replace(/(?<![A-Za-z0-9_])(\*|_|~|`)([^*_~`\n]+)\1(?![A-Za-z0-9_])/g, '$2'); return /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/u.test(t) ? <Emo text={t} size="1em"/> : t; })() : <span className="italic">No messages yet</span>}
                          </p>
                          {unreadCounts[conv.id] > 0 && (
                            <span className="flex-shrink-0 min-w-4 h-4 px-1 bg-teal-500 rounded-full text-white flex items-center justify-center font-bold shadow" style={{fontSize:'9px'}}>
                              {unreadCounts[conv.id] > 99 ? '99+' : unreadCounts[conv.id]}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* PEOPLE */}
            {showPanel==='sidebar' && activeTab==='people' && (
              <div className="p-2">
                {/* Message yourself — pinned on top */}
                {!search && (
                  <div className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-teal-50 dark:hover:bg-teal-900/20 cursor-pointer transition border-b border-gray-100 dark:border-gray-700 mb-1"
                    onClick={() => startChat(user?.id)}>
                    <Avatar name={user?.full_name} url={user?.avatar_url} size={10}/>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{user?.full_name} <span className="text-teal-600 dark:text-teal-400 font-medium">(You)</span></p>
                      <p className="text-xs text-gray-400 truncate">Message yourself · Save notes and files</p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); startChat(user?.id); }} className="text-teal-500 hover:text-teal-600 text-lg flex-shrink-0 p-1">💬</button>
                  </div>
                )}
                {filteredUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition" onClick={() => openProfilePanel(u, true)}>
                    <Avatar name={u.full_name} url={u.avatar_url} size={10} online={isOnline(u.id)}/>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{u.full_name}</p>
                      <p className="text-xs text-gray-400 truncate">
                      {(() => {
                        const status = userStatuses[u.id] || u.status;
                        if (status === 'away') return <span className="text-yellow-500">🟡 Away</span>;
                        if (status === 'dnd') return <span className="text-red-500">🔴 Do Not Disturb</span>;
                        if (status === 'meeting') return <span className="text-blue-500">🔵 In a Meeting</span>;
                        if (status === 'lunch') return <span className="text-orange-400">🍽️ Out for Lunch</span>;
                        if (status === 'vacation') return <span className="text-purple-400">🏖️ Vacation</span>;
                        return u.designation||u.department||u.email;
                      })()}
                    </p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); startChat(u.id); }} className="text-teal-500 hover:text-teal-600 text-lg flex-shrink-0 p-1">💬</button>
                  </div>
                ))}
              </div>
            )}

            {/* CALLS */}
            {showPanel==='sidebar' && activeTab==='calls' && (
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto">
                  {!Array.isArray(callHistory) || callHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center mb-5 shadow-lg">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10 text-teal-400">
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                        </svg>
                      </div>
                      <p className="text-base font-bold text-gray-600 dark:text-gray-300">No recent calls</p>
                      <p className="text-xs text-gray-400 mt-2 leading-relaxed">Voice & video call history appears here</p>
                    </div>
                  ) : callHistory.map(call => {
                    const isOut = call.direction === 'outgoing';
                    const displayStatus = call.display_status || call.status;
                    const missed = displayStatus === 'missed' || displayStatus === 'no_answer';
                    const name = call.other_user_name;
                    const avatar = call.other_user_avatar;
                    const otherId = call.other_user_id;
                    const incoming = !isOut;
                    const dur = call.duration_seconds > 0
                      ? `${Math.floor(call.duration_seconds/60)}:${String(call.duration_seconds%60).padStart(2,'0')}`
                      : null;
                    // pg type parser returns correct ISO string with IST offset
                    const dateObj = call.started_at ? new Date(call.started_at) : null;
                    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    const dateStr = dateObj && !isNaN(dateObj)
                      ? `${dateObj.getDate()} ${months[dateObj.getMonth()]}, ${String(dateObj.getHours()).padStart(2,'0')}:${String(dateObj.getMinutes()).padStart(2,'0')}`
                      : '';
                    return (
                      <div key={call.id} className="group flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-all border-b border-gray-100 dark:border-gray-800/30 last:border-0">
                        {/* Avatar with status ring */}
                        <div className="relative flex-shrink-0">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm overflow-hidden shadow
                            ${missed ? 'ring-2 ring-red-400/60' : 'ring-2 ring-transparent'}`}
                            style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                            {avatar
                              ? <img src={avatar && avatar.startsWith('http') ? avatar : avatar ? `https://qtalk.qualtechedge.in${avatar}` : ''} className="w-full h-full object-cover" alt="" onError={e=>e.target.style.display='none'}/>
                              : (name?.[0]||'?').toUpperCase()}
                          </div>
                          {/* Call type + direction badge */}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center shadow-sm
                            ${missed ? 'bg-red-500' : call.status==='completed' ? 'bg-green-500' : 'bg-gray-400'}`}>
                            {isOut
                              ? <svg viewBox="0 0 24 24" fill="white" className="w-2.5 h-2.5"><path d="M2 12l20-10-10 20V12z"/></svg>
                              : <svg viewBox="0 0 24 24" fill="white" className="w-2.5 h-2.5"><path d="M22 12L2 2l10 20V12z"/></svg>}
                          </div>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-bold truncate leading-tight ${missed ? 'text-red-500 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
                            {name}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`text-xs font-semibold
                              ${missed ? 'text-red-400' : call.status==='completed' ? 'text-green-500' : 'text-gray-400'}`}>
                              {displayStatus==='missed' ? '↙ Missed' : displayStatus==='rejected' || displayStatus==='declined' ? '✕ Declined' : isOut ? '↗ Outgoing' : '↙ Incoming'}
                            </span>
                            <span className="text-gray-300 dark:text-gray-600">·</span>
                            <span className="text-xs text-gray-400">{call.call_type==='video' ? '📹' : '🎙️'}</span>
                            {dur && <><span className="text-gray-300 dark:text-gray-600">·</span><span className="text-xs text-gray-400">{dur}</span></>}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{dateStr}</p>
                        </div>

                        {/* Call back buttons */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => {
                              const u = users.find(x => x.id === otherId);
                              if (u) setCallModal({ isInitiating:true, targetUser:u, callType:'voice' });
                            }}
                            className="p-1.5 rounded-lg bg-teal-50 dark:bg-teal-900/30 text-teal-500 hover:bg-teal-100 transition"
                            title="Voice call">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                            </svg>
                          </button>
                          <button onClick={() => {
                              const u = users.find(x => x.id === otherId);
                              if (u) setCallModal({ isInitiating:true, targetUser:u, callType:'video' });
                            }}
                            className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-500 hover:bg-blue-100 transition"
                            title="Video call">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* MEETINGS — Calendar view */}
            {showPanel==='sidebar' && activeTab==='meetings' && (
              <MeetingsCalendar
                meetings={meetings}
                onSchedule={() => {
                  const mid = generateMeetId();
                  setMeetingForm(f=>({...f, meet_link:`https://qtalk.qualtechedge.in/meet/${mid}`, _meetId:mid}));
                  setShowMeetingModal(true);
                }}
                onRespond={respondMeeting}
              />
            )}
          </div>

        </div>
      )}

            {/* MAIN CHAT */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-950 relative"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        {/* Drag & drop overlay */}
        {isDragging && activeConversation && (
          <div className="absolute inset-0 z-50 bg-teal-500/20 border-4 border-dashed border-teal-400 rounded-2xl flex flex-col items-center justify-center pointer-events-none">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-teal-500 mb-3">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p className="text-teal-600 dark:text-teal-400 font-bold text-xl">Drop to send file</p>
            <p className="text-teal-500 text-sm mt-1">Release to upload</p>
          </div>
        )}

        {!activeConversation ? (
          <div className="flex-1 flex flex-col items-center justify-center relative overflow-hidden"
            style={{background: darkMode
              ? 'radial-gradient(ellipse at 30% 20%, #0d948820 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, #1d4ed820 0%, transparent 60%), #030712'
              : 'radial-gradient(ellipse at 30% 20%, #ccfbf1 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, #dbeafe 0%, transparent 60%), #f8fafc'}}>

            {/* Decorative blobs */}
            <div className="absolute top-20 left-20 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}/>
            <div className="absolute bottom-20 right-20 w-48 h-48 rounded-full opacity-10 blur-3xl pointer-events-none" style={{background:'linear-gradient(135deg,#8b5cf6,#0d9488)'}}/>

            <div className="relative flex flex-col items-center max-w-md text-center px-8 z-10">
              {/* Logo */}
              <div className="relative mb-8">
                <div className="absolute inset-0 rounded-3xl blur-2xl opacity-30" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}/>
                <img src="/logo.png" alt="Qtalk" className="relative object-contain" style={{width:'100px',height:'100px'}}/>
              </div>

              {/* Greeting */}
              <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight mb-1">
                Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.full_name?.split(' ')[0]} 👋
              </h1>
              <p className="text-base text-gray-400 dark:text-gray-500 leading-relaxed mb-8">
                Your secure workspace for QualTechEdge
              </p>

              {/* CTA buttons */}
              <div className="flex gap-3 mb-10">
                <button onClick={() => { setActiveTab('chats'); setShowPanel('newchat'); }}
                  className="flex items-center gap-2 px-6 py-3 text-white text-sm font-bold rounded-2xl hover:opacity-90 transition-all hover:scale-105 shadow-lg shadow-teal-500/25"
                  style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                  New Chat
                </button>
                <button onClick={() => setShowGroupModal(true)}
                  className="flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-2xl border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-teal-400 hover:text-teal-600 dark:hover:text-teal-400 transition-all hover:scale-105 bg-white/50 dark:bg-gray-800/50 backdrop-blur">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                  New Group
                </button>
              </div>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>, label:'End-to-end encrypted'},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>, label:'Unlimited file sharing'},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>, label:'Built-in meetings'},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M22 17H2a3 3 0 003-3V9a7 7 0 0114 0v5a3 3 0 003 3zm-8.27 4a2 2 0 01-3.46 0"/></svg>, label:'Team announcements'},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>, label:'Task manager'},
                ].map(f => (
                  <span key={f.label} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 shadow-sm">
                    <span>{f.icon}</span>{f.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* CHAT HEADER */}
            <div className="px-5 py-3.5 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 shadow-sm">
              <div className="cursor-pointer" onClick={() => {
                if (activeConversation.type === 'group') setShowGroupInfo(true);
                else if (activeConversation.other_user) openProfilePanel(activeConversation.other_user);
              }}>
                <Avatar name={activeConversation.name} url={activeConversation.avatar_url} size={10} isGroup={activeConversation.type==='group'} online={activeConversation.type!=='group' && activeConversation.other_user && isOnline(activeConversation.other_user.id)}/>
              </div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => {
                if (activeConversation.type === 'group') setShowGroupInfo(true);
                else if (activeConversation.other_user) openProfilePanel(activeConversation.other_user);
              }}>
                <p className="font-bold text-gray-800 dark:text-gray-100 text-base hover:text-teal-600 dark:hover:text-teal-400 transition">{activeConversation.name||'Chat'}</p>
                <p className="text-xs text-gray-400 flex items-center gap-2">
                  <span>
                    {typingInConv ? <span className="text-teal-500 animate-pulse">{typingInConv.name} is typing...</span>
                      : activeConversation.other_user && isOnline(activeConversation.other_user.id)
                        ? (() => {
                            const st = userStatuses[activeConversation.other_user.id];
                            if (st === 'dnd') return <span className="text-red-500 font-medium">● Do Not Disturb</span>;
                            if (st === 'away') return <span className="text-yellow-500 font-medium">● Away</span>;
                            if (st === 'meeting') return <span className="text-blue-500 font-medium">● In a Meeting</span>;
                            if (st === 'lunch') return <span className="text-orange-400 font-medium">● Out for Lunch</span>;
                            if (st === 'vacation') return <span className="text-purple-400 font-medium">● On Vacation</span>;
                            return <span className="text-green-500 font-medium">● Online</span>;
                          })()
                        : activeConversation.type==='group' ? 'Group chat' : 'Offline'}
                  </span>
                  <span className="text-teal-500 flex items-center gap-0.5 text-xs font-medium">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg> End-to-end encrypted
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-1 relative">
                {/* Voice call */}
                {activeConversation.type==='group' && (
                  <button onClick={startGroupCall}
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition"
                    title="Start group call">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                      <line x1="18" y1="2" x2="18" y2="8"/><line x1="15" y1="5" x2="21" y2="5"/>
                    </svg>
                  </button>
                )}
                {activeConversation.type==='direct' && (
                  <>
                    <button onClick={() => startCall('voice')}
                      className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition relative"
                      title="Voice call (Beta)">
                      <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-amber-400 text-white px-1 rounded-full leading-tight">β</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                    </button>

                  </>
                )}
                <button onClick={() => { const opening=!searchInChat; setSearchInChat(opening); setChatSearchQuery(''); if(chatSearchRef.current)chatSearchRef.current.value=''; if(opening) ensureFullHistoryForSearch(); setTimeout(()=>chatSearchRef.current?.focus(),50); }}
                  className={`p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition ${searchInChat?'bg-teal-50 dark:bg-teal-900/30 text-teal-500':'text-gray-500 dark:text-gray-400'}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </button>
                <div className="relative">
                  <button onClick={() => setShowChatMenu(!showChatMenu)}
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                  </button>
                  {showChatMenu && (
                    <div className="absolute right-0 top-10 w-52 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-40 overflow-hidden" onMouseLeave={() => setShowChatMenu(false)}>
                      {[
                        {label:'Search in chat', action:()=>{setSearchInChat(true);ensureFullHistoryForSearch();setShowChatMenu(false);}},
                        
                        ...(activeConversation.other_user?[{label:'View profile', action:()=>{openProfilePanel(activeConversation.other_user);setShowChatMenu(false);}}]:[]),
                        {label:'Clear chat', action:()=>{
            setShowChatMenu(false);
            askConfirm({
              title: 'Clear chat',
              message: 'Clear this chat for you only? The other person will still keep their copy of the messages.',
              confirmLabel: 'Clear',
              danger: true,
              onConfirm: async () => {
                if (!activeConversation?.id) { setMessages([]); return; }
                try {
                  await axios.post(`${API_URL}/chat/conversations/${activeConversation.id}/clear`, {}, { headers: getAuthHeader() });
                  setMessages([]);
                  updateConversation({ id: activeConversation.id, last_message: null, last_message_time: null, last_message_type: null });
                  toast.success('Chat cleared');
                } catch(e) { toast.error('Failed to clear chat'); }
              }
            });
          }},
                        {label:'Close chat', action:()=>{setActiveConversation(null);setShowChatMenu(false);setShowProfilePanel(false);}, danger:true},
                      ].map((item,i) => (
                        <button key={i} onClick={item.action}
                          className={`w-full flex items-center px-4 py-3 text-sm transition hover:bg-gray-50 dark:hover:bg-gray-700 ${item.danger?'text-red-500':'text-gray-700 dark:text-gray-200'}`}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SEARCH BAR */}
            {searchInChat && (
              <div className="flex items-center gap-2 px-5 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-100 dark:border-yellow-800">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-yellow-600 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input type="text" placeholder="Search in conversation..."
                  ref={chatSearchRef}
                  defaultValue=""
                  onChange={e => setChatSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none"/>
                {chatSearchQuery && <span className="text-xs text-gray-400">{messages.filter(m => { try { const d = decryptMessage(m.content); return d?.toLowerCase().includes(chatSearchQuery.toLowerCase()); } catch(e){ return false; } }).length} results</span>}
                <button onClick={() => {setSearchInChat(false);setChatSearchQuery('');if(chatSearchRef.current)chatSearchRef.current.value='';}} className="p-1 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-800 text-yellow-700 transition">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            )}

            {/* MESSAGES */}
            <div id="qtalk-chat-messages" className="flex-1 overflow-y-auto px-5 py-4" style={{...getChatBg()}}
              onScroll={onMessagesScroll}
              onClick={() => setMsgActionMenu(null)}>
              {messages.length===0 && (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <span className="text-5xl mb-3">👋</span>
                  <p className="text-sm font-medium">Say hello to start the conversation!</p>
                </div>
              )}
              {selectMode && (
                <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2.5 mb-2 rounded-2xl bg-white dark:bg-gray-800 border border-teal-200 dark:border-teal-800 shadow-lg">
                  <button onClick={exitSelectMode} title="Cancel"
                    className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 transition">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <span className="text-sm font-bold text-gray-800 dark:text-gray-100">{selectedMsgIds.length} selected</span>
                  <span className="text-xs text-gray-400 hidden sm:inline">· Tap messages to select</span>
                  <div className="flex-1"/>
                  <button onClick={() => { if (selectedMsgIds.length) setForwardMsg(messages.filter(m => selectedMsgIds.includes(m.id))); }}
                    disabled={!selectedMsgIds.length}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition disabled:opacity-40 shadow"
                    style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 004 4h12"/></svg>
                    Forward
                  </button>
                </div>
              )}
              {(() => {
                const convUnread = openUnreadCountRef.current || 0;
                const firstUnreadIdx = convUnread > 0 ? Math.max(0, messages.length - convUnread) : -1;
                let unreadShown = false;
                return messages.map((msg, idx) => {
                const isMe = Number(msg.sender_id) === Number(user?.id);
                const prevMsg = messages[idx-1];
                const showDateSep = !prevMsg || !isSameDay(new Date(msg.created_at), new Date(prevMsg.created_at));
                const showAvatar = !isMe && (!prevMsg || prevMsg.sender_id !== msg.sender_id);
                if (chatSearchQuery) {
                  try {
                    const decrypted = decryptMessage(msg.content) || '';
                    if (!decrypted.toLowerCase().includes(chatSearchQuery.toLowerCase())) return null;
                  } catch(e) { return null; }
                }
                const showUnreadMarker = !unreadShown && !isMe && idx === firstUnreadIdx;
                if (showUnreadMarker) unreadShown = true;
                const _isSel = selectMode && selectedMsgIds.includes(msg.id);
                return (
                  <div key={msg.id}
                    onClickCapture={selectMode ? (e) => { e.stopPropagation(); e.preventDefault(); toggleMsgSelect(msg.id); } : undefined}
                    className={selectMode ? `relative cursor-pointer transition pl-10 -mx-2 px-2 rounded-lg ${_isSel ? 'bg-teal-500/10' : 'hover:bg-gray-500/5'}` : ''}>
                    {selectMode && !msg.is_deleted && (
                      <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${_isSel ? 'bg-teal-500 border-teal-500' : 'border-gray-400 dark:border-gray-500 bg-white/80 dark:bg-gray-800/80'}`}>
                          {_isSel && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </div>
                      </div>
                    )}
                    {showUnreadMarker && (
                      <div ref={el => { if (el && !firstUnreadRef.current) firstUnreadRef.current = el; }} className="flex items-center gap-3 my-3">
                        <div className="flex-1 h-px bg-teal-400/40"/>
                        <span className="text-xs text-teal-500 font-semibold bg-teal-50 dark:bg-teal-900/20 px-3 py-1 rounded-full border border-teal-200 dark:border-teal-700">
                          {convUnread} unread message{convUnread > 1 ? 's' : ''}
                        </span>
                        <div className="flex-1 h-px bg-teal-400/40"/>
                      </div>
                    )}
                    {showDateSep && (
                      <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"/>
                        <span className="text-xs text-gray-400 bg-white dark:bg-gray-900 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 font-medium shadow-sm">
                          {getDateLabel(msg.created_at)}
                        </span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"/>
                      </div>
                    )}
                    {msg.message_type === 'system' ? (
                      <div className="flex justify-center my-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/80 px-4 py-1 rounded-full">
                          {msg.content}
                        </span>
                      </div>
                    ) : (
                      <MessageBubble
                        key={msg.id}
                        msg={msg}
                        isMe={isMe}
                        showAvatar={showAvatar}
                        conversationType={activeConversation.type}
                        hoveredMsg={hoveredMsg}
                        setHoveredMsg={setHoveredMsg}
                        msgActionMenu={msgActionMenu}
                        setMsgActionMenu={setMsgActionMenu}
                        setReplyTo={setReplyTo}
                        startEdit={startEdit}
                        deleteMessage={deleteMessage}
                        pinMessage={pinMessage}
                        addReaction={addReaction}
                        onShowReactors={showReactors}
                        setLightbox={setLightbox}
                        setZoomLevel={setZoomLevel}
                        chatSearchQuery={chatSearchQuery}
                        setForwardMsg={setForwardMsg}
                        onEnterSelect={enterSelectMode}
                        setShowMsgInfo={setShowMsgInfo}
                        mentionNames={mentionMembers.map(m => m.full_name)}
                      />
                    )}
                  </div>
                );
              });
              })()}
              {/* Typing indicator — renders inline only while active (no permanent reserved space) */}
              {typingInConv && (
                <div className="flex items-end gap-2 mb-2 mt-1">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {typingInConv.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex flex-col items-start">
                    {activeConversation.type === 'group' && (
                      <span className="text-xs text-gray-400 mb-1 ml-1">{typingInConv.name}</span>
                    )}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}}/>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}/>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}/>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} id="messages-end"/>
            </div>

            {/* INPUT AREA */}
            <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              {replyTo && (
                <div className="flex items-center gap-3 px-5 py-2.5 bg-teal-50 dark:bg-teal-900/20 border-b-2 border-teal-200 dark:border-teal-700">
                  <div className="w-1 h-10 bg-teal-500 rounded-full flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-teal-600 dark:text-teal-400 mb-0.5">
                      ↩ Replying to {Number(replyTo.sender_id)===Number(user?.id) ? 'yourself' : replyTo.sender_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {replyTo.message_type==='image' ? '📷 Photo'
                        : replyTo.message_type==='file' ? `📄 ${replyTo.file_name}`
                        : decryptMessage(replyTo.content) || '📎 Attachment'}
                    </p>
                  </div>
                  <button onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-red-500 p-1 transition flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              )}
              {editingMsg && (
                <div className="flex items-center gap-3 px-5 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
                  <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">✏️ Editing message</span>
                  <button onClick={() => { setEditingMsg(null); setMessageInput(''); }} className="ml-auto text-gray-400 hover:text-gray-600 p-1">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              )}
              <div className="px-5 py-3">
                {showEmoji && (
                  <div className="absolute bottom-20 z-50 rounded-xl overflow-hidden shadow-2xl" style={{left:showPanel?'420px':'80px'}}>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Emoji</span>
                      <button onClick={() => setShowEmoji(false)}
                        className="w-6 h-6 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-gray-600 transition">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                    <EmojiPicker onEmojiClick={e => setMessageInput(prev => prev+e.emoji)}
                      theme={darkMode?'dark':'light'} emojiStyle="apple" height={330} width={300} skinTonesDisabled previewConfig={{showPreview:false}} lazyLoadEmojis/>
                  </div>
                )}
                <div className="flex items-end gap-2 relative">
                  <button onClick={() => setShowEmoji(!showEmoji)} className={`p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition flex-shrink-0 ${showEmoji?'bg-teal-50 dark:bg-teal-900/30':''}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-500 dark:text-gray-400"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                  </button>
                  <div className="flex-shrink-0">
                    <button onClick={() => fileInputRef.current?.click()} className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-500 dark:text-gray-400"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                    </button>
                    <input ref={fileInputRef} type="file" multiple onChange={handleFileUpload} className="hidden"/>
                  </div>
                  {/* @ Mention dropdown */}
                  {showMentions && filteredMentions.length > 0 && (
                    <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-100 dark:border-gray-700"
                      style={{background: darkMode ? '#1e293b' : 'white'}}>
                      <div className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-xs font-semibold text-gray-400">Mention a member</p>
                      </div>
                      {filteredMentions.map((m, i) => (
                        <div key={m.id} onClick={() => insertMention(m)}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition"
                          style={{background: i === mentionIndex ? (darkMode ? 'rgba(13,148,136,0.15)' : '#f0fdf9') : 'transparent'}}>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                            {m.avatar_url
                              ? <img src={m.avatar_url?.startsWith('https://') ? m.avatar_url : `https://qtalk.qualtechedge.in${m.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                              : m.full_name?.[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{m.full_name}</p>
                            <p className="text-xs text-gray-400 truncate">{m.designation || m.department || 'Member'}</p>
                          </div>
                          {m.is_admin && <span className="text-xs px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-md font-medium">Admin</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea ref={textareaRef} value={messageInput} onChange={handleTyping} onKeyDown={handleKeyDown}
                    placeholder={editingMsg?"Edit message...":"Type a message..."} rows={1}
                    autoFocus
                    spellCheck={true}
                    autoCorrect="on"
                    autoCapitalize="sentences"
                    onPaste={handlePaste}
                    className="flex-1 px-4 py-2.5 rounded-2xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition resize-none text-sm"
                    style={{maxHeight:'120px',overflowY:'auto'}}/>
                  <button onClick={sendMessage} disabled={!messageInput.trim()}
                    className="p-3 rounded-xl bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white transition disabled:opacity-40 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* GROUP INFO PANEL */}
      {showGroupInfo && activeConversation?.type === 'group' && (
        <GroupInfoPanel
          conversation={activeConversation}
          currentUser={user}
          users={users}
          onClose={() => setShowGroupInfo(false)}
          onLeave={() => {
            setActiveConversation(null);
            fetchConversations();
          }}
          onUpdate={(updated) => {
            setActiveConversation(prev => ({ ...prev, ...updated }));
            fetchConversations();
          }}
        />
      )}

      {/* RIGHT PROFILE PANEL */}
      {showProfilePanel && profilePanelUser && (
        <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex flex-col">

          {/* Hero header */}
          <div className="relative" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
            <div className="px-4 pt-3 pb-10 flex flex-col items-center">
              {/* Back button */}
              <div className="w-full flex justify-between items-center mb-3">
                <button onClick={() => setShowProfilePanel(false)}
                  className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                </button>
                <span className="text-white/80 text-xs font-semibold tracking-wide">Contact Info</span>
                <div className="w-8"/>
              </div>
              {/* Avatar */}
              <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white/40 shadow-xl border-2 border-white/20 cursor-pointer hover:opacity-90 transition mb-2"
                onClick={() => profilePanelUser.avatar_url && setLightbox(profilePanelUser.avatar_url?.startsWith('https://')?profilePanelUser.avatar_url:`https://qtalk.qualtechedge.in${profilePanelUser.avatar_url}`)}>
                {profilePanelUser.avatar_url?.startsWith('https://')
                  ? <img src={profilePanelUser.avatar_url?.startsWith('https://')?profilePanelUser.avatar_url:`https://qtalk.qualtechedge.in${profilePanelUser.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                  : <div className="w-full h-full flex items-center justify-center bg-white/20">
                      <span className="text-3xl font-bold text-white">{profilePanelUser.full_name?.[0]?.toUpperCase()}</span>
                    </div>}
              </div>
              <h3 className="font-bold text-white text-base leading-tight">{profilePanelUser.full_name}</h3>
              {profilePanelUser.designation && <p className="text-xs text-white/70 mt-0.5">{profilePanelUser.designation}{profilePanelUser.department ? ` · ${profilePanelUser.department}` : ''}</p>}
              <div className="flex items-center gap-1.5 mt-1">
                <span className={`w-2 h-2 rounded-full ${isOnline(profilePanelUser.id)?'bg-green-400':'bg-white/40'}`}/>
                <span className="text-xs text-white/70">
                  {isOnline(profilePanelUser.id) ? 'Online now' : profilePanelUser.last_seen ? `Last seen ${format(new Date(profilePanelUser.last_seen),'MMM d, HH:mm')}` : 'Offline'}
                </span>
              </div>
            </div>
            {/* Wave */}
            <div className="absolute bottom-0 left-0 right-0 h-5 bg-white dark:bg-gray-900 rounded-t-3xl"/>
          </div>

          {/* Action buttons */}
          <div className="px-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex gap-2 mt-1">
              {profilePanelUser.id !== user?.id && (
                <button onClick={() => { startChat(profilePanelUser.id); setShowProfilePanel(false); }}
                  className="flex-1 flex flex-col items-center gap-1 py-2.5 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-xl hover:bg-teal-100 dark:hover:bg-teal-900/30 transition">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                  Message
                </button>
              )}
              <button onClick={() => { startCall('voice', profilePanelUser); setShowProfilePanel(false); }}
                className="flex-1 flex flex-col items-center gap-1 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition relative">
                <span className="absolute top-1 right-1 text-[7px] font-bold bg-amber-400 text-white px-1 rounded-full leading-tight">β</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                Voice
              </button>
              <button onClick={() => { startCall('video', profilePanelUser); setShowProfilePanel(false); }}
                className="flex-1 flex flex-col items-center gap-1 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition relative">
                <span className="absolute top-1 right-1 text-[7px] font-bold bg-amber-400 text-white px-1 rounded-full leading-tight">β</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                </svg>
                Video
              </button>
              <button onClick={() => setShowUserCalendar(true)}
                className="flex-1 flex flex-col items-center gap-1 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Calendar
              </button>
            </div>
          </div>
          <div className="flex border-b border-gray-100 dark:border-gray-700">
            {['info'].concat(activeConversation && profilePanelUser && (activeConversation.other_user?.id === profilePanelUser.id || activeConversation.type==='group') ? ['media'] : []).map(tab => (
              <button key={tab} onClick={() => {
                setProfileTab(tab);
                if (tab === 'media' && activeConversation) fetchSharedMedia(activeConversation.id);
              }}
                className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wide transition ${profileTab===tab?'text-teal-600 dark:text-teal-400 border-b-2 border-teal-500':'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                {tab==='info' ? (
                  <span className="flex items-center justify-center gap-1">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    Info
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    Media
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {profileTab==='info' && (
              <div className="p-3 space-y-2">
                {(() => {
                  const st = userStatuses[profilePanelUser.id] || profilePanelUser.status;
                  if (!st || st === 'online') return null;
                  const presets = ['away','dnd','meeting','lunch','vacation'];
                  if (presets.includes(st)) return (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-2.5 flex items-center gap-2">
                      <span className="text-sm">{st==='away'?'🟡':st==='dnd'?'🔴':st==='meeting'?'🔵':st==='lunch'?'🍽️':'🏖️'}</span>
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                        {st==='away'?'Away':st==='dnd'?'Do Not Disturb':st==='meeting'?'In a Meeting':st==='lunch'?'Out for Lunch':'On Vacation'}
                      </p>
                    </div>
                  );
                  return (
                    <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-2.5 flex items-center gap-2">
                      <span className="text-sm">💬</span>
                      <p className="text-xs text-gray-600 dark:text-gray-300 italic">"{st}"</p>
                    </div>
                  );
                })()}
                {[
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-teal-500"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>, label:'Email', val:profilePanelUser.email},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-blue-500"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>, label:'Mobile', val:profilePanelUser.mobile},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-purple-500"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, label:'Department', val:profilePanelUser.department},
                  {icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-orange-500"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>, label:'Designation', val:profilePanelUser.designation},
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-700 flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100 dark:border-gray-600">
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">
                        {item.val || <span className="text-gray-400 italic font-normal">Not set</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {profileTab==='media' && (
              <div className="p-3">
                {sharedMedia.length===0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400"><span className="text-4xl mb-2">🖼️</span><p className="text-sm">No media shared yet</p></div>
                ) : (
                  <div>
                    {sharedMedia.filter(m=>m.message_type==='image').length>0 && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Photos</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {sharedMedia.filter(m=>m.message_type==='image').map(m => (
                            <div key={m.id} className="aspect-square rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition"
                              onClick={() => { setLightbox(m.file_url?.startsWith('https://')?m.file_url:`https://qtalk.qualtechedge.in${m.file_url}`); setZoomLevel(1); }}>
                              <img src={m.file_url?.startsWith('https://')?m.file_url:`https://qtalk.qualtechedge.in${m.file_url}`} alt={m.file_name} className="w-full h-full object-cover"/>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {sharedMedia.filter(m=>m.message_type==='file').length>0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Files</p>
                        <div className="space-y-2">
                          {sharedMedia.filter(m=>m.message_type==='file').map(m => (
                            <a key={m.id} href={m.file_url?.startsWith('https://')?m.file_url:`https://qtalk.qualtechedge.in${m.file_url}`} download={m.file_name} target="_blank" rel="noreferrer"
                              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                              <span className="text-2xl flex-shrink-0">{getFileIcon(m.file_mime_type)?.icon || '📎'}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{m.file_name}</p>
                                <p className="text-xs text-gray-400">{formatFileSize(m.file_size)} · {format(new Date(m.created_at),'MMM d, yyyy')}</p>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CALENDAR TAB — show user's upcoming meetings */}
            {profileTab==='calendar' && (
              <div className="p-3">
                {/* Upcoming meetings with this person */}
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block"/>
                  Shared Meetings
                </p>
                {(() => {
                  const sharedMeetings = meetings.filter(m =>
                    new Date(m.scheduled_at) >= new Date() &&
                    m.my_status !== 'declined' &&
                    (m.organizer_name === profilePanelUser.full_name || m.is_organizer)
                  );
                  if (sharedMeetings.length === 0) return (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10 mb-2 opacity-20">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <p className="text-xs">No upcoming shared meetings</p>
                      <button onClick={() => {
                        setShowProfilePanel(false);
                        const mid = generateMeetId();
                        setMeetingForm(f=>({...f,
                          meet_link:`https://qtalk.qualtechedge.in/meet/${mid}`,
                          participant_ids:[profilePanelUser.id]
                        }));
                        setShowMeetingModal(true);
                      }} className="mt-2 text-xs text-teal-500 hover:text-teal-600 font-semibold transition">
                        + Schedule with {profilePanelUser.full_name.split(' ')[0]}
                      </button>
                    </div>
                  );
                  return sharedMeetings.map(m => {
                    const meetDate = new Date(m.scheduled_at);
                    const todayMeet = isToday(meetDate);
                    return (
                      <div key={m.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-3 mb-2 shadow-sm">
                        <div className="flex items-start gap-2.5">
                          <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex flex-col items-center justify-center text-white font-bold ${todayMeet ? 'bg-teal-500' : 'bg-blue-500'}`}>
                            <span style={{fontSize:'9px',lineHeight:1}}>{format(meetDate,'MMM').toUpperCase()}</span>
                            <span style={{fontSize:'14px',lineHeight:1.1}}>{format(meetDate,'d')}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{m.title}</p>
                            <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">{format(meetDate,'HH:mm')} · {m.duration_minutes} min</p>
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${m.my_status==='accepted'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>
                            {m.my_status==='accepted'?'✓':'●'}
                          </span>
                        </div>
                        {m.meet_link && (
                          <a href={m.meet_link.startsWith('http') ? m.meet_link : `https://qtalk.qualtechedge.in${m.meet_link}`}
                            target="_blank" rel="noreferrer"
                            className="mt-2 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-teal-500 to-blue-500 hover:opacity-90 rounded-lg transition">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                            </svg>
                            Join Meeting
                          </a>
                        )}
                      </div>
                    );
                  });
                })()}

                {/* Action buttons */}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => {
                    setShowProfilePanel(false);
                    const mid = generateMeetId();
                    setMeetingForm(f=>({...f,
                      meet_link:`https://qtalk.qualtechedge.in/meet/${mid}`,
                      participant_ids:[profilePanelUser.id]
                    }));
                    setShowMeetingModal(true);
                  }} className="flex-1 py-2.5 flex items-center justify-center gap-1.5 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-xs font-semibold rounded-xl hover:opacity-90 transition">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Schedule
                  </button>
                  <button onClick={() => {
                    setShowProfilePanel(false);
                    setActiveTab('meetings');
                    setShowPanel('sidebar');
                  }} className="flex-1 py-2.5 flex items-center justify-center gap-1.5 border-2 border-teal-200 dark:border-teal-700 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-xl hover:bg-teal-50 dark:hover:bg-teal-900/20 transition">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    Full Calendar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showSettings && <SettingsModal onClose={() => { setShowSettings(false); setSearch(''); document.activeElement?.blur(); }}/>}
      {/* Message Info Modal */}
      {showMsgInfo && (
        <MsgInfoModal
          msgId={showMsgInfo}
          conversationId={activeConversation?.id}
          users={users}
          currentUserId={user?.id}
          onClose={() => setShowMsgInfo(null)}
        />
      )}

      {reactorsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-4" style={{zIndex:9999}} onClick={() => setReactorsModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-xs overflow-hidden flex flex-col" style={{maxHeight:'70vh'}} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="font-bold text-gray-800 dark:text-white text-sm">Reactions</h3>
              <button onClick={() => setReactorsModal(null)} className="w-6 h-6 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-gray-600 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="overflow-y-auto p-2">
              {reactorsModal.reactions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No reactions</p>
              ) : (
                reactorsModal.reactions.map(r => (
                  <div key={r.emoji} className="px-2 py-1.5">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{r.emoji}</span>
                      <span className="text-xs text-gray-400 font-medium">{r.count}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 pl-7">
                      {(r.users || []).map((name, i) => (
                        <span key={i} className="text-xs text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 rounded-full px-2 py-0.5">{name}</span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <Lightbox src={lightbox}
        images={(Array.isArray(messages)?messages:[]).filter(m => m.message_type==='image' && m.file_url && !m.is_deleted).map(m => m.file_url.startsWith('http') ? m.file_url : `https://qtalk.qualtechedge.in${m.file_url}`)}
        onNavigate={(u) => { setLightbox(u); setZoomLevel(1); }}
        zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} onClose={() => setLightbox(null)}/>

      {/* Paste image preview modal */}
      {pastedImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center backdrop-blur-sm"
          onClick={() => setPastedImage(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <p className="font-semibold text-gray-800 dark:text-white text-sm">📋 Send Screenshot</p>
              <button onClick={() => setPastedImage(null)} className="text-gray-400 hover:text-gray-600 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {/* Image preview */}
            <div className="p-4">
              <img src={pastedImage.preview} alt="Screenshot" className="w-full max-h-96 object-contain rounded-xl bg-gray-50 dark:bg-gray-800"/>
            </div>
            {/* Caption input */}
            <div className="px-4 pb-4">
              <div className="relative flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-600 px-4 py-2">
                {captionMentionQuery !== null && (() => {
                  const matches = mentionMembers.filter(m => m.id !== user?.id && (captionMentionQuery === '' || m.full_name.toLowerCase().includes(captionMentionQuery.toLowerCase()))).slice(0,6);
                  if (matches.length === 0) return null;
                  return (
                    <div className="absolute bottom-full mb-2 left-0 right-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50 max-h-48 overflow-y-auto">
                      {matches.map(m => (
                        <button key={m.id} type="button" onMouseDown={ev => { ev.preventDefault(); insertCaptionMention(m); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{(m.full_name?.[0]||'?').toUpperCase()}</div>
                          <span className="text-gray-800 dark:text-gray-100">{m.full_name}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <input type="text" placeholder="Add a caption..." value={pastedImage.caption}
                  onChange={e => { const val = e.target.value; setPastedImage(prev => ({...prev, caption: val})); const c = e.target.selectionStart; const mm = val.slice(0, c).match(/@(\w*)$/); setCaptionMentionQuery(mm && activeConversation?.type === 'group' ? mm[1] : null); }}
                  onKeyDown={e => { if(e.key==='Escape'){ if(captionMentionQuery!==null) setCaptionMentionQuery(null); else setPastedImage(null); return; } if(e.key==='Enter') sendPastedImage(); }}
                  autoFocus
                  className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none"/>
                <button onClick={sendPastedImage}
                  className="p-2 rounded-xl bg-gradient-to-r from-teal-500 to-blue-500 text-white transition hover:opacity-90">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"/>
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5 text-center">Press Enter to send · Esc to cancel</p>
            </div>
          </div>
        </div>
      )}

      {/* File send confirmation modal */}
      {pendingFiles.length > 0 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-sm border border-gray-100 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-1">
              {pendingFiles.length === 1 ? 'Send File?' : `Send ${pendingFiles.length} files?`}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              To <span className="font-semibold text-teal-500">{activeConversation?.name}</span>
              {pendingFiles.length > 1 && <span className="text-gray-400"> · caption applies to the selected file</span>}
            </p>

            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl mb-3">
              {pendingPreviews[pendingIndex] ? (
                <img src={pendingPreviews[pendingIndex]} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0"/>
              ) : (
                <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-2xl flex-shrink-0">📎</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{pendingFiles[pendingIndex]?.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{((pendingFiles[pendingIndex]?.size || 0) / 1024).toFixed(1)} KB</p>
              </div>
            </div>

            {pendingFiles.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                {pendingFiles.map((f, i) => (
                  <button key={i} type="button" onClick={() => { setPendingIndex(i); setFileMentionQuery(null); }}
                    className={`relative w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden border-2 transition
                      ${i === pendingIndex ? 'border-teal-500' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    {pendingPreviews[i]
                      ? <img src={pendingPreviews[i]} alt="" className="w-full h-full object-cover"/>
                      : <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-lg">📎</div>}
                    {(fileCaptions[i] || '').trim() && (
                      <span className="absolute bottom-0 right-0 w-4 h-4 bg-teal-500 rounded-tl-md flex items-center justify-center">
                        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="relative mb-4">
              {fileMentionQuery !== null && (() => {
                const matches = mentionMembers.filter(m => m.id !== user?.id && (fileMentionQuery === '' || m.full_name.toLowerCase().includes(fileMentionQuery.toLowerCase()))).slice(0,6);
                if (matches.length === 0) return null;
                return (
                  <div className="absolute bottom-full mb-2 left-0 right-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50 max-h-48 overflow-y-auto">
                    {matches.map(m => (
                      <button key={m.id} type="button" onMouseDown={ev => { ev.preventDefault(); insertFileMention(m); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{(m.full_name?.[0]||'?').toUpperCase()}</div>
                        <span className="text-gray-800 dark:text-gray-100">{m.full_name}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
              <input
                type="text"
                value={fileCaptions[pendingIndex] || ''}
                onChange={e => {
                  const val = e.target.value;
                  const cur = e.target.selectionStart;
                  setFileCaptions(prev => ({ ...prev, [pendingIndex]: val }));
                  const mm = val.slice(0, cur).match(/@(\w*)$/);
                  setFileMentionQuery(mm && activeConversation?.type === 'group' ? mm[1] : null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') { if (fileMentionQuery !== null) setFileMentionQuery(null); else closePendingFiles(); return; }
                  if (e.key === 'Enter') { e.preventDefault(); if (fileMentionQuery === null) confirmSendFile(); }
                }}
                placeholder="Add a caption (optional)"
                autoFocus
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
              />
            </div>

            <div className="flex gap-3">
              <button onClick={closePendingFiles}
                className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                Cancel
              </button>
              <button onClick={confirmSendFile}
                className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl text-sm font-semibold hover:opacity-90 transition">
                Send{pendingFiles.length > 1 ? ` (${pendingFiles.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GLOBAL UPLOAD TRAY ── */}
      {uploads.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2" style={{minWidth:'280px',maxWidth:'320px'}}>
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold text-white"
            style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              {uploads.filter(u=>u.status==='uploading').length > 0
                ? `Uploading ${uploads.filter(u=>u.status==='uploading').length} file${uploads.filter(u=>u.status==='uploading').length>1?'s':''}...`
                : 'Uploads complete'}
            </div>
            <button onClick={clearDoneUploads} className="text-white/70 hover:text-white transition text-xs">
              {uploads.every(u=>u.status!=='uploading') ? 'Clear all' : ''}
            </button>
          </div>

          {/* Upload items */}
          {uploads.map(u => (
            <div key={u.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-3">
              <div className="flex items-start gap-2.5 mb-2">
                {/* File icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-bold
                  ${u.status==='error' ? 'bg-red-500' : u.status==='done' ? 'bg-teal-500' : 'bg-blue-500'}`}>
                  {u.status==='done' ? '✓' : u.status==='error' ? '✕' : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{u.fileName}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {u.convName || 'Chat'} · {u.status==='done' ? 'Sent!' : u.status==='error' ? 'Failed' : `${u.progress}%`}
                  </p>
                </div>
                <button onClick={() => removeUpload(u.id)} className="text-gray-300 hover:text-red-400 transition flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 rounded-full overflow-hidden" style={{background:'#e5e7eb'}}>
                <div className="h-full rounded-full transition-all duration-300"
                  style={{
                    width:`${u.progress}%`,
                    background: u.status==='error' ? '#ef4444' : u.status==='done' ? '#10b981' : 'linear-gradient(90deg,#0d9488,#2563eb)',
                    transition:'width 0.3s ease'
                  }}/>
              </div>
            </div>
          ))}
        </div>
      )}
      {showUserCalendar && profilePanelUser && (
        <UserCalendarModal
          user={profilePanelUser}
          onClose={() => setShowUserCalendar(false)}
          onSchedule={() => {
            setShowUserCalendar(false);
            const mid = generateMeetId();
            setMeetingForm(f=>({...f,
              meet_link:`https://qtalk.qualtechedge.in/meet/${mid}`,
              participant_ids:[profilePanelUser.id]
            }));
            setShowMeetingModal(true);
          }}
        />
      )}
      <ForwardModal
        msg={forwardMsg}
        conversations={conversations}
        users={users}
        currentUserId={user?.id}
        onForward={forwardMessage}
        onClose={() => setForwardMsg(null)}
      />

      {/* Notification Panel — fixed top-right, above everything */}
      {showNotifPanel && (
        <div className="fixed top-4 right-4 w-96 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
          style={{maxHeight:'520px', zIndex:9999}}>
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900 dark:text-white text-sm">Notifications</h3>
                {unreadNotifs > 0 && (
                  <span className="px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-full font-bold" style={{fontSize:'10px'}}>
                    {unreadNotifs} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <button onClick={() => { setNotifications([]); setUnreadNotifs(0); localStorage.removeItem('qtalk_notifications'); }}
                    className="text-xs text-red-400 hover:text-red-500 font-semibold transition px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20">
                    Clear all
                  </button>
                )}
                {unreadNotifs > 0 && (
                  <button onClick={markAllNotifsRead}
                    className="text-xs text-teal-500 hover:text-teal-600 font-semibold transition px-2 py-1 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20">
                    Mark read
                  </button>
                )}
                <button onClick={() => setShowNotifPanel(false)} className="w-6 h-6 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-400 hover:text-gray-600 transition">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            {/* Type filter pills */}
            <div className="flex gap-1.5 flex-wrap">
              {[
                {id:'all', label:'All', icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px'}}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>},
                {id:'message', label:'Messages', icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px'}}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>},
                {id:'meeting', label:'Meetings', icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px'}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>},
                {id:'task', label:'Tasks', icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px'}}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>},
                {id:'announcement', label:'Feed', icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px'}}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>},
              ].map(f => {
                const count = f.id==='all' ? notifications.length : notifications.filter(n=>n.type===f.id||n.type===f.id+'_assigned'||n.type===f.id+'_comment'||n.type==='mention'&&f.id==='announcement').length;
                return (
                  <button key={f.id}
                    onClick={() => setNotifFilter(f.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition"
                    style={{
                      background: notifFilter===f.id ? 'linear-gradient(135deg,#0d9488,#2563eb)' : darkMode?'rgba(255,255,255,0.05)':'#f1f5f9',
                      color: notifFilter===f.id ? 'white' : darkMode?'rgba(255,255,255,0.5)':'#6b7280'
                    }}>
                    {f.icon} {f.label} {count>0&&<span className="opacity-70">({count})</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="overflow-y-auto" style={{maxHeight:'440px'}}>
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 mb-3 opacity-30">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">All caught up!</p>
                <p className="text-xs text-gray-400 mt-1">No new notifications</p>
              </div>
            ) : (
              notifications.filter(n => {
                if (notifFilter === 'all') return true;
                if (notifFilter === 'message') return n.type === 'message';
                if (notifFilter === 'meeting') return n.type === 'meeting';
                if (notifFilter === 'task') return n.type === 'task' || n.type === 'task_assigned';
                if (notifFilter === 'announcement') return n.type === 'announcement' || n.type === 'announcement_comment' || n.type === 'mention';
                return true;
              }).map(n => (
                <div key={n.id}
                  onClick={() => {
                    // Mark as read
                    setNotifications(prev => prev.map(x => x.id===n.id ? {...x,read:true} : x));
                    if (n.type === 'task_assigned' || n.type === 'task') {
                      window.location.href = '/todos';
                    } else if (n.type === 'announcement_comment' || n.type === 'mention') {
                      window.location.href = n.link || '/announcements';
                    } else if (n.type === 'announcement') {
                      window.location.href = '/announcements';
                    } else if (n.type === 'meeting') {
                      const mid = n.meeting_id || (n.data && n.data.meeting_id);
                      window.location.href = mid ? `/calendar?meeting=${mid}` : '/calendar';
                    } else if (n.conversation_id) {
                      const conv = conversations.find(c => c.id === n.conversation_id);
                      if (conv) {
                        setActiveConversation(conv);
                        setShowPanel('sidebar');
                        setActiveTab('chats');
                        fetchMessages(conv.id);
                      }
                    }
                    setShowNotifPanel(false);
                  }}
                  className={`flex items-start gap-3 px-4 py-3.5 border-b border-gray-50 dark:border-gray-800/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors
                    ${!n.read ? 'bg-teal-50/40 dark:bg-teal-900/10' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold
                    ${n.type==='message' ? 'bg-gradient-to-br from-teal-400 to-blue-500 text-white'
                    : n.type==='meeting' ? 'bg-gradient-to-br from-teal-500 to-blue-600 text-white'
                    : n.type==='task_assigned' ? 'bg-gradient-to-br from-green-500 to-teal-500 text-white'
                    : n.type==='announcement' ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                    : 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'}`}>
                    {n.type==='message' ? (n.avatar || '?')
                    : n.type==='meeting' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    ) : n.type==='announcement' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                      </svg>
                    ) : '⏰'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{n.title}</p>
                      <p className="text-gray-300 dark:text-gray-600 flex-shrink-0" style={{fontSize:'10px'}}>{format(n.time, 'HH:mm')}</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{n.body}</p>
                    {n.type==='meeting' && n.meeting_id && n.my_status !== 'accepted' && n.my_status !== 'declined' && (
                      <div className="flex gap-2 mt-2" onClick={e=>e.stopPropagation()}>
                        <button onClick={async()=>{
                          try {
                            await axios.put(`${API_URL}/meetings/${n.meeting_id}/respond`,{status:'accepted'},{headers:getAuthHeader()});
                            setNotifications(prev=>prev.map(x=>x.id===n.id?{...x,my_status:'accepted',read:true}:x));
                            toast.success('Meeting accepted!');
                          } catch(e){ toast.error('Failed'); }
                        }} className="px-3 py-1 rounded-lg text-xs font-bold text-white" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)',border:'none',cursor:'pointer'}}>
                          ✓ Accept
                        </button>
                        <button onClick={async()=>{
                          try {
                            await axios.put(`${API_URL}/meetings/${n.meeting_id}/respond`,{status:'declined'},{headers:getAuthHeader()});
                            setNotifications(prev=>prev.map(x=>x.id===n.id?{...x,my_status:'declined',read:true}:x));
                            toast.success('Meeting declined');
                          } catch(e){ toast.error('Failed'); }
                        }} className="px-3 py-1 rounded-lg text-xs font-bold" style={{background:'rgba(239,68,68,0.12)',color:'#ef4444',border:'1px solid rgba(239,68,68,0.2)',cursor:'pointer'}}>
                          ✗ Decline
                        </button>
                      </div>
                    )}
                    {n.type==='meeting' && n.my_status === 'accepted' && (
                      <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full font-semibold" style={{background:'rgba(16,185,129,0.12)',color:'#10b981'}}>✓ Accepted</span>
                    )}
                    {n.type==='meeting' && n.my_status === 'declined' && (
                      <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full font-semibold" style={{background:'rgba(239,68,68,0.1)',color:'#ef4444'}}>✗ Declined</span>
                    )}
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0 mt-1.5 shadow-sm"/>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Call Modal */}
      {callModal && (
        <CallModal
          key="call-modal-singleton"
          call={callModal.call}
          currentUser={user}
          targetUser={callModal.targetUser}
          callType={callModal.callType}
          isInitiating={callModal.isInitiating}
          onClose={() => { setCallModal(null); }}
        />
      )}

      {/* In-app confirmation modal — centered, theme-aware (replaces native window.confirm) */}
      {confirmModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setConfirmModal(null)}>
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_0.15s_ease-out]">
            <div className="p-5">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">{confirmModal.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{confirmModal.message}</p>
            </div>
            <div className="flex gap-2 px-5 pb-5">
              <button onClick={() => setConfirmModal(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                Cancel
              </button>
              <button onClick={() => { const fn = confirmModal.onConfirm; setConfirmModal(null); if (fn) fn(); }}
                className={`flex-1 py-2.5 rounded-xl text-white font-semibold text-sm transition ${confirmModal.danger ? 'bg-red-500 hover:bg-red-600' : 'bg-gradient-to-r from-teal-500 to-blue-500 hover:opacity-90'}`}>
                {confirmModal.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <GroupModal show={showGroupModal} onClose={() => setShowGroupModal(false)}
        users={users} onCreate={createGroup}/>

      <MeetingModal show={showMeetingModal} onClose={() => setShowMeetingModal(false)}
        meetingForm={meetingForm} setMeetingForm={setMeetingForm}
        onSubmit={handleMeetingSubmit} users={users}/>
    </div>
  );
}
